import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import { first, isNumber, omit } from 'lodash';
import { FormattedMessage, type IntlShape } from 'react-intl';
import {
  CalibrationInfo,
  CalibrationItem,
  PerformanceProgressCompletion,
} from 'types';
import { formatChangeDelta } from 'utils/custom-question/formatter';
import {
  getCalibrationFlags,
  PreCompiledAutoCalibrationRule,
} from 'utils/models/Calibration';
import {
  getCampaignHasFeatureEnabled,
  PERFORMANCE_FEATURE_ASSESS_MANAGER,
  PERFORMANCE_FEATURE_ASSESS_ORGANIZATION,
  PERFORMANCE_FEATURE_COMPLETE_OBJECTIVES_FOR_QUARTER_STEP,
  PERFORMANCE_FEATURE_COMPLETE_OBJECTIVES_STEP,
  PERFORMANCE_FEATURE_COMPLETE_RESUME_STEP,
  PERFORMANCE_FEATURE_CONTRIBUTION_SELF_REFLECTIONS,
  PERFORMANCE_FEATURE_DISABLE_CALLOUTS,
  PERFORMANCE_FEATURE_DISABLE_NETWORK,
  PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED,
  PERFORMANCE_FEATURE_EVALUATION_OPEN_RESPONSE_QUESTIONS,
  PERFORMANCE_FEATURE_HIGH_PRIORITY_PEERS,
  PERFORMANCE_FEATURE_INCLUDE_BASICS_PAGE,
  PERFORMANCE_FEATURE_MAXIMUM_PEERS_TO_SHOW,
  PERFORMANCE_FEATURE_NETWORK_STAKEHOLDERS,
  PERFORMANCE_FEATURE_OTHERS_OPEN_RESPONSE_QUESTIONS,
  PERFORMANCE_FEATURE_PEER_FEEDBACK,
  PERFORMANCE_FEATURE_PEER_FEEDBACK_ATTRIBUTION,
  PERFORMANCE_FEATURE_SELF_OPEN_RESPONSE_QUESTIONS,
  PERFORMANCE_FEATURE_UPWARD_MANAGER_OPEN_RESPONSE_QUESTIONS,
  PHASE_TYPE_CALIBRATION,
  PHASE_TYPE_EVALUATION,
  PHASE_TYPE_OTHERS,
  PHASE_TYPE_REPORTING,
  PHASE_TYPE_SELF,
  WIZARD_TYPE_ENGAGEMENT_ONLY,
  WIZARD_TYPE_ENPS_ONLY,
  WIZARD_TYPE_ONA_ONLY,
  WizardTypes,
} from 'utils/models/PerformanceUtils';
import { SENSITIVE_DATA_OMITTED } from 'utils/models/SurveyResponse';
import { FeatureContext } from 'utils/util/features';
import { getIconForFieldName } from 'views/Widgets/People/Filters/common';
import { AvatarBasicPerson } from 'views/Widgets/People/UnlinkedAvatar';
import { ICONS, TEAM_PERFORMANCE_DASHBOARD } from '../../../../consts/consts';
import {
  CAMPAIGN_STATUSES,
  getPhaseByType,
  getQuestionFromCampaign,
  getQuestionResponseDict,
  SORTABLE_FIELD_SUFFIX,
} from '../../../../utils/models/Campaign';
import {
  CAMPAIGN_DEFAULT_MAX_PEERS,
  CompletionStats,
  CUSTOM_QUESTION_NONANONYMOUS_PREFIX,
  getCampaignFeature,
  getCampaignHasFeatureEnabledDefaultOn,
  getDirectReportsEligibleForFeedback,
  getOthersPhaseProgress,
  getPerformanceFeatureEnabled,
  getPerformanceFeatureEnabledAndNonEmtpy,
  getPerformanceFeatureEnabledWithFallback,
  getStepCompleted,
  isDirectReportEvaluatedByManager,
  isPersonInPeopleList,
  isRatedByManager,
  SurveyResponseCompletionLookup,
  sortRelationshipsByPriority,
} from '../../../../utils/models/Performance';
import {
  PERSON_METADATA_FIELDS,
  peopleObjectsAreEqual,
} from '../../../../utils/models/Person';
import {
  PACKET_STATUS_DENIED,
  PACKET_STATUS_WITHDRAWN,
  PACKET_STATUSES,
} from '../../../../utils/models/PromotionPackets';
import {
  getPersonIsParticipatingInPhase,
  relationshipIsActive,
  relationshipIsCompleted,
} from '../../../../utils/models/Relationship';
import { RELATIONSHIP_TYPES } from '../../../../utils/models/RelationshipUtils';
import { getDateForTimeInRoleComputation } from '../../../../utils/util/time';
import {
  getAverageForObjectValues,
  isRawValueFollowedByOutputValueArrayOfTwo,
  isStrictlyObject,
  monthDiff,
  stripHtml,
} from '../../../../utils/util/util';
import PersonCardList from '../../../Widgets/People/PersonCardList';
import { FilterOptions, matchesParsed } from '../../Filters/sexpr';
import RichTextViewer from '../../Inputs/RichTextViewer';
import { INPUT_TYPES } from '../../Inputs/ValidatedInputTypes';
import {
  getDescriptionForFieldName,
  getEnhancedDescriptionForFieldName,
} from '../../People/Table/commons';
import ScrollableUncontrolledPopover from '../../ScrollableUncontrolledPopover';
import { CATCHALL_GROUP_NAME } from '../TakeawaysDashboard/util';
import CalibrationPopover from './Calibration/CalibrationPopover';
import TeamDashboardCalibrationEditor from './Calibration/TeamDashboardCalibrationEditor';
import {
  decoupleValAndCalibrationInfo,
  IMPORTANT_ANSWER,
  mergeCalibrationInfos,
  NO_PRIORITY_ANSWER,
} from './Calibration/utils';
import {
  defaultFormatRelationshipItem,
  formatCSVONACountsByType,
  formatCSVONARelationshipByType,
  formatGiverEmail,
  formatSkills,
  getFilterAbbreviationForType,
} from './common';

const MAX_REVIEWS_TO_WRITE_BEFORE_WAITLIST = CAMPAIGN_DEFAULT_MAX_PEERS;

export const SELF_PHASE_QUESTION_PREFIX = '_self_phase_';
export const PEER_FEEDBACK_QUESTION_PREFIX = '_peer_feedback_';
export const UPWARD_FEEDBACK_QUESTION_PREFIX = '_upward_feedback_';
export const EVALUATION_PHASE_QUESTION_PREFIX = '_evaluation_phase_';

// keep this sortable
export const SHARE_STATUS_ACKNOWLEDGED_WITHOUT_RATING = 0.75;
export const SHARE_STATUS_RELEASED = 0.5;
export const SHARE_STATUS_WITHHOLD_REPORT_RELEASE = 0.25;
export const SHARE_STATUS_ONLY_RECEIVING_REVIEWS = 0;
export const SHARE_STATUS_NOT_PARTICIPATING = -1000;
export const SHARE_STATUS_IGNORE_DUE_TO_NO_MANAGER = -1001;

export const buildTeamDashboardLink = (filters) => {
  const filtersQueryParameter = filters.map(([fieldName, fieldValue]) => {
    const filterAbbreviation = getFilterAbbreviationForType(fieldName);
    if (filterAbbreviation) {
      return `${filterAbbreviation}-${fieldValue}`;
    }
    return '';
  });
  if (filtersQueryParameter) {
    const querystring = new URLSearchParams(
      {
        filters: filtersQueryParameter,
      },
      // @ts-expect-error
      {
        arrayFormat: 'bracket',
      }
    ).toString();
    return `${TEAM_PERFORMANCE_DASHBOARD().path}?${querystring}`;
  }
};

export const isPersonType = (type) => {
  return (
    type === 'people' ||
    type === 'manager' ||
    type === 'manager_or_above' ||
    type === 'final_rating_provided_by_person'
  );
};

export const matchesFilter = (type, s, id) => {
  if (s._index !== type) {
    return false;
  }

  return isPersonType(s._index)
    ? s?.object?.email === id || s?.object?.external_id === id
    : s?.name === id;
};

export const getFilterSerializedId = (filter) => {
  // for people, manager, manager_or_above, and final_rating_provided_by_person,
  // use email if it exists, otherwise external id
  if (isPersonType(filter._index)) {
    return filter?.object?.email || filter?.object?.external_id;
  }

  // for everything else, use the string itself (the name)
  return filter?.name;
};

const peerOverlayCallback = (ap: AvatarBasicPerson) => {
  // @ts-expect-error
  if (ap?.is_completed)
    return (
      <div>
        <i className="fe fe-check text-success me-1"></i>
        <FormattedMessage
          id="app.views.widgets.dashboards.team_dashboard.helpers.peer_review_completed"
          defaultMessage="Peer review completed"
        />
      </div>
    );
  // @ts-expect-error
  else if (ap?.is_declined)
    return (
      <div>
        <i className="fe fe-x text-danger me-1"></i>
        <FormattedMessage
          id="app.views.widgets.dashboards.team_dashboard.helpers.peer_review_declined"
          defaultMessage="Peer review declined"
        />
      </div>
    );
  return (
    <div>
      <FormattedMessage
        id="app.views.widgets.dashboards.team_dashboard.helpers.peer_review_not_completed"
        defaultMessage="Peer review not completed"
      />
    </div>
  );
};

// for the "Calibration column" filters
const CALIBRATION_STATUS_NO_FLAGS_RAISED = 1;
const CALIBRATION_STATUS_NEEDS_ATTENTION = 2;
const CALIBRATION_STATUS_CALIBRATED = 3;
const CALIBRATION_STATUS_RECALCULATING = 4;

// for the Report status filter
const REPORT_STATUS_NOT_RELEASED = 1;
const REPORT_STATUS_RELEASED = 2;
const REPORT_STATUS_CONVERSATION_NOT_ACKNOWLEDGED = 1;
const REPORT_STATUS_CONVERSATION_ACKNOWLEDGED = 2;

// for "artificial" rating values not related to the rating scale
// (NOTE: we allow negative and zero ratings, so we use strings to
// ensure these values are unique), we use zz_<char>_* to ensure it sorts
// at the end after other ratings and the order of these is consistent with past behavior
export const RATING_NOT_PARTICIPATING = 'zz_A_RATING_NOT_PARTICIPATING'; // Not participating
export const RATING_NOT_YET_COMPLETE = 'zz_B_RATING_NOT_YET_COMPLETE'; // Rating incomplete
export const RATING_IGNORE_DUE_TO_NO_MANAGER =
  'zz_C_RATING_IGNORE_DUE_TO_NO_MANAGER'; // No manager
export const RATING_NOT_ALLOWED = 'zz_D_RATING_NOT_ALLOWED'; // No rating allowed (aka "new to role")
export const RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW =
  'zz_E_RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW'; // Manually set as ineligible to manager review

export const getRatingDisplayComponent = (
  rating,
  isFinalRating,
  uniqueId,
  formatMessage
) => {
  switch (rating) {
    case RATING_NOT_PARTICIPATING:
      return (
        <>
          <span id={'row-role-final-rating-not-eligible-' + uniqueId}>
            <i className={ICONS.NOT_ELIGIBLE} />
          </span>
          <UncontrolledPopover
            placement="left"
            trigger="hover"
            target={'row-role-final-rating-not-eligible-' + uniqueId}
          >
            <FormattedMessage
              id="app.views.widgets.dashboards.team_dashboard.not_eligible_to_participate_in_this_cycle"
              defaultMessage="Not eligible to participate in this cycle"
            />
          </UncontrolledPopover>
        </>
      );
    case RATING_NOT_YET_COMPLETE:
      if (isFinalRating) {
        return formatMessage({
          id: 'app.views.widgets.dashboards.team_dashboard.not_yet_complete',
          description: 'To be decided',
          defaultMessage: 'TBD',
        });
      } else {
        return '?';
      }
    case RATING_IGNORE_DUE_TO_NO_MANAGER:
      return '-';
    case RATING_NOT_ALLOWED:
      return '-';
    case RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW:
      return '-';
    default:
      return rating;
  }
};

export const isRealNumericRating = (rating) => {
  return (
    rating !== RATING_NOT_PARTICIPATING &&
    rating !== RATING_NOT_YET_COMPLETE &&
    rating !== RATING_IGNORE_DUE_TO_NO_MANAGER &&
    rating !== RATING_NOT_ALLOWED &&
    rating !== RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW
  );
};

export const getSortableRating = (rating) => {
  if (isNumber(rating)) {
    return rating;
  }

  // assume rating ranges don't go that high, but we can increase it in case
  const baseRating = 1000000000;
  switch (rating) {
    case RATING_NOT_PARTICIPATING:
      return baseRating + 1;
    case RATING_NOT_YET_COMPLETE:
      return baseRating + 2;
    case RATING_IGNORE_DUE_TO_NO_MANAGER:
      return baseRating + 3;
    case RATING_NOT_ALLOWED:
      return baseRating + 4;
    case RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW:
      return baseRating + 5;
    default:
      return baseRating + 6;
  }
};

const getFinalRatingText = (finalRatingValue, formatMessage) => {
  if (typeof finalRatingValue === 'undefined') {
    return undefined;
  }

  if (
    finalRatingValue === RATING_NOT_PARTICIPATING ||
    finalRatingValue === RATING_IGNORE_DUE_TO_NO_MANAGER ||
    finalRatingValue === RATING_NOT_ALLOWED
  ) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.final_rating.not_eligible',
      defaultMessage: 'Not eligible for rating',
    });
  } else if (finalRatingValue === RATING_NOT_YET_COMPLETE) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.final_rating.not_yet_complete',
      defaultMessage: 'Rating not yet complete',
    });
  } else if (finalRatingValue === RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.final_rating.not_eligible_to_manager_review',
      defaultMessage:
        'This person was manually marked as ineligible to receive a manager evaluation',
    });
  } else {
    return formatMessage(
      {
        id: 'app.views.widgets.dashboard.team_dashboard.final_rating.default',
        defaultMessage: '{finalRatingValue} final rating',
      },
      { finalRatingValue }
    );
  }
};

export interface TeamDashboardColumnDescriptor {
  field: string;
  name: string | JSX.Element;
  // Fuller label (icon + descriptive text) shown by the column selector modal
  // in place of `name`. Use when `name` is an icon only, or too terse to be
  // identifiable on its own (e.g. "Mgr" -> "Manager").
  fullName?: string | JSX.Element;
  popoverContent?: string | JSX.Element;
  csvName?: string;
  [key: string]: unknown;
}

export interface CustomColumnGenerator {
  columnConfig: TeamDashboardColumnDescriptor;
  insert_before?: string;
  insert_after?: string;
  // A column whose displayed value cannot be ranked - an object, an element -
  // maps it to something sortable here. Written to the row under the
  // _sortable convention, which the column then points `sortField` at.
  toSortValue?: (value: any) => number | string | null;
  generateValue: (options: {
    formatMessage: any;
    shouldSourceMetadataFromCampaignUnlessUndefined: any;
    campaign: any;
    phasesNotOpenTextDict: any;
    surveyResponse: any;
    incomingRelationships: any;
    outgoingRelationships: any;
    promotionPackets: any;
    demoPeople: any;
    surveyResponseLookup: any;
    disableConversationAcknowledgements: any;
    completion_stats: CompletionStats;
    enpsResponsesLookup: SurveyResponseCompletionLookup;
    engagementSurveyResponsesLookup: SurveyResponseCompletionLookup;
    preCompiledautoCalibrationRules: PreCompiledAutoCalibrationRule[];
    featuresContext: FeatureContext;
    surveyResponseCalibrationItems: CalibrationItem[];
  }) => any;
}

export const getPersonFromPeopleDataset = (
  formatMessage,
  shouldSourceMetadataFromCampaignUnlessUndefined,
  campaign,
  phasesNotOpenTextDict,
  surveyResponse,
  incomingRelationships = [],
  outgoingRelationships = [],
  promotionPackets,
  demoPeople,
  surveyResponseLookup,
  disableConversationAcknowledgements,
  completion_stats: CompletionStats,
  enpsResponsesLookup: SurveyResponseCompletionLookup,
  engagementSurveyResponsesLookup: SurveyResponseCompletionLookup,
  preCompiledautoCalibrationRules: PreCompiledAutoCalibrationRule[],
  featuresContext: FeatureContext,
  surveyResponseCalibrationItems: CalibrationItem[],
  ratingsHistory: any,
  customColumnsGenerators?: CustomColumnGenerator[],
  // true between a rule change and the recompute of the cached flags,
  // while the cached flags may describe the wrong rule
  calibrationFlagsRecomputing = false
) => {
  const survey_response_id = surveyResponse?.id;
  const surveyResponsePerson = surveyResponse.person;
  const configs = surveyResponse.configs;
  const rawResponses = surveyResponse.responses;
  const perfProgressCompletions = new Set<PerformanceProgressCompletion>(
    surveyResponse.configs?.completions ?? []
  );

  let selfPhaseResponses = {
    [SELF_PHASE_QUESTION_PREFIX + 'positive_skills']:
      surveyResponse?.positive_skills?.length > 0
        ? surveyResponse.positive_skills.map((sr) => sr.name).join(', ')
        : null,
    [SELF_PHASE_QUESTION_PREFIX + 'negative_skills']:
      surveyResponse?.negative_skills?.length > 0
        ? surveyResponse.negative_skills.map((sr) => sr.name).join(', ')
        : null,
  };

  // note: we don't want to include anonymous questions here but they
  // should not be passed in from the backend, so we don't need to filter
  // them out here
  for (const questionKey in rawResponses) {
    if (
      questionKey !== 'positive_skills' &&
      questionKey !== 'negative_skills'
    ) {
      selfPhaseResponses = {
        ...selfPhaseResponses,
        ...getQuestionResponseDict(
          formatMessage,
          campaign,
          PHASE_TYPE_SELF,
          PERFORMANCE_FEATURE_SELF_OPEN_RESPONSE_QUESTIONS,
          questionKey,
          rawResponses[questionKey],
          SELF_PHASE_QUESTION_PREFIX + questionKey,
          survey_response_id,
          surveyResponsePerson,
          surveyResponseCalibrationItems.filter((ci) =>
            ci.key.startsWith(questionKey)
          ),
          NO_PRIORITY_ANSWER
        ),
      };
    }
  }

  const person = surveyResponsePerson;
  // propagate status to for filtering purposes
  // The best thing would be to do a spread copy of surveyResponsePerson,
  // but I'm not sure what I'm going to break by doing that
  person['current_status'] = person['current_status'] ?? person['status'];

  // mark the person since some data has been sanitized.
  // This is used when the person is seeing their own data
  person[SENSITIVE_DATA_OMITTED] = !!surveyResponse?.[SENSITIVE_DATA_OMITTED];
  // NOTE: these relationships are auto-generated when the dataset is saved
  const managers =
    shouldSourceMetadataFromCampaignUnlessUndefined || !person.manager
      ? outgoingRelationships
          // @ts-expect-error
          .filter((r) => r.dataset && r.type === RELATIONSHIP_TYPES.REPORTS_TO)
          // @ts-expect-error
          .map((r) => r.to_person)
      : person.manager
        ? [person.manager]
        : [];

  const manager = !(managers?.length > 0)
    ? null
    : {
        ...managers[0],
        external_id:
          shouldSourceMetadataFromCampaignUnlessUndefined ||
          !managers[0].manager_external_id
            ? configs?.manager_external_id
            : managers[0].manager_external_id,
        effective_date:
          shouldSourceMetadataFromCampaignUnlessUndefined ||
          !managers[0].manager_effective_date
            ? configs?.manager_effective_date
            : managers[0].manager_effective_date,
      };

  const othersPhaseProgress = getOthersPhaseProgress(
    person,
    [...incomingRelationships, ...outgoingRelationships],
    completion_stats,
    surveyResponseLookup,
    enpsResponsesLookup,
    engagementSurveyResponsesLookup,
    campaign
  );

  // calculate additional fields that will be in CSV export
  const perf_progress_others_phase_gave_manager_feedback =
    othersPhaseProgress.others.upward.completed.length > 0;
  const perf_progress_others_phase_outgoing_peers_assigned =
    othersPhaseProgress.others.peer.requested.length;
  const perf_progress_others_phase_outgoing_peers_completed =
    othersPhaseProgress.others.peer.completed.length;
  const perf_progress_others_phase_outgoing_peers_declined =
    othersPhaseProgress.others.peer.declined.length;

  const eligibleDirectReports = getDirectReportsEligibleForFeedback(
    person,
    campaign,
    incomingRelationships.filter(relationshipIsActive),
    surveyResponseLookup,
    demoPeople,
    true
  );

  const numEligibleDirectReports = eligibleDirectReports?.length;

  const numDirectReportsCompleted = outgoingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset &&
      // @ts-expect-error
      (r.type === RELATIONSHIP_TYPES.HAS_DIRECT_REPORT_FEEDBACK_FOR ||
        // @ts-expect-error
        r.type ===
          RELATIONSHIP_TYPES.HAS_ADDITIONAL_MANAGER_DIRECT_REPORT_FEEDBACK_FOR) &&
      relationshipIsCompleted(r, campaign) &&
      relationshipIsActive(r) &&
      // @ts-expect-error
      isPersonInPeopleList(r.to_person, eligibleDirectReports)
  )?.length;

  const selfPhase = getPhaseByType(campaign, PHASE_TYPE_SELF);
  const personIsParticipatingInSelfPhase = selfPhase
    ? getPersonIsParticipatingInPhase(person, configs, selfPhase)
    : false;

  const othersPhase = getPhaseByType(campaign, PHASE_TYPE_OTHERS);
  const personIsParticipatingInOthersPhase =
    othersPhase &&
    getPersonIsParticipatingInPhase(person, configs) &&
    ((getCampaignHasFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_PEER_FEEDBACK
    ) &&
      othersPhaseProgress.others.peer.requested.length > 0) ||
      (getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_ASSESS_MANAGER
      ) &&
        othersPhaseProgress.others.upward.requested.length > 0) ||
      (getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_ASSESS_ORGANIZATION
      ) &&
        configs?.is_assessing_org !== false) ||
      (getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED
      ) &&
        configs?.is_assessing_org !== false));

  const personIsParticipatingInOthersAltPhase =
    othersPhase &&
    getPersonIsParticipatingInPhase(person, configs) &&
    ((getCampaignHasFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_PEER_FEEDBACK
    ) &&
      othersPhaseProgress.others_alt.peer.requested.length > 0) ||
      (getCampaignHasFeatureEnabled(
        campaign,
        PERFORMANCE_FEATURE_ASSESS_MANAGER
      ) &&
        othersPhaseProgress.others_alt.upward.requested.length > 0));

  const evaluationPhase = getPhaseByType(campaign, PHASE_TYPE_EVALUATION);
  const personIsParticipatingInEvaluationPhase = evaluationPhase
    ? getPersonIsParticipatingInPhase(
        person,
        configs,
        evaluationPhase,
        numEligibleDirectReports
      )
    : false;

  const personIsActiveAndParticipatingInAnyPhase =
    personIsParticipatingInSelfPhase ||
    personIsParticipatingInOthersPhase ||
    (numEligibleDirectReports > 0 && personIsParticipatingInEvaluationPhase);

  const personIsOnlyReceivingReview =
    !!configs?.is_only_receiving_review ||
    (!!evaluationPhase && !othersPhase && !selfPhase);

  const hasCompleteResumeStep = getPerformanceFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_COMPLETE_RESUME_STEP
  );

  const hasSelfOpenResponseQuestionsStep =
    getPerformanceFeatureEnabledAndNonEmtpy(
      campaign,
      PERFORMANCE_FEATURE_SELF_OPEN_RESPONSE_QUESTIONS
    );

  const hasCompleteObjectivesStep = getPerformanceFeatureEnabledWithFallback(
    campaign,
    [
      PERFORMANCE_FEATURE_COMPLETE_OBJECTIVES_FOR_QUARTER_STEP,
      PERFORMANCE_FEATURE_COMPLETE_OBJECTIVES_STEP,
    ]
  );

  const hasSelfReflectionsStep = getPerformanceFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_CONTRIBUTION_SELF_REFLECTIONS
  );

  const highPriorityPeersStep =
    othersPhase &&
    getPerformanceFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_HIGH_PRIORITY_PEERS
    ) &&
    numEligibleDirectReports > 0
      ? 1
      : 0;

  const networkStep =
    getPerformanceFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_DISABLE_NETWORK
    ) &&
    !getCampaignHasFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_NETWORK_STAKEHOLDERS
    )
      ? 0
      : 1;

  const calloutsStep = getPerformanceFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_DISABLE_CALLOUTS
  )
    ? 0
    : 1;

  const selfAssessStep =
    hasCompleteResumeStep ||
    hasSelfOpenResponseQuestionsStep ||
    hasSelfReflectionsStep ||
    hasCompleteObjectivesStep
      ? 1
      : 0;

  const basicsStep = getCampaignHasFeatureEnabledDefaultOn(
    campaign,
    PERFORMANCE_FEATURE_INCLUDE_BASICS_PAGE
  )
    ? 1
    : 0;

  const baseSelfPhaseSteps =
    1 +
    basicsStep +
    selfAssessStep +
    highPriorityPeersStep +
    networkStep +
    calloutsStep;

  // people managers with high priority peers have an extra step
  const selfPhaseMaxSteps =
    !phasesNotOpenTextDict[PHASE_TYPE_SELF] && personIsParticipatingInSelfPhase
      ? baseSelfPhaseSteps
      : -1;

  const enpsStep = getPerformanceFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED
  )
    ? 1
    : 0;

  const enpsCompleted = getStepCompleted(
    enpsStep > 0,
    othersPhaseProgress.enps,
    surveyResponse.configs['phase_others_complete']
  );

  const engagementSurveyStep = getPerformanceFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_ASSESS_ORGANIZATION
  )
    ? 1
    : 0;

  const engagementSurveyCompleted = getStepCompleted(
    engagementSurveyStep > 0,
    othersPhaseProgress.engagement_survey,
    surveyResponse.configs['phase_others_complete']
  );

  let maxPeers = MAX_REVIEWS_TO_WRITE_BEFORE_WAITLIST;
  const campaignMaxPeers = getCampaignFeature(
    campaign,
    PERFORMANCE_FEATURE_MAXIMUM_PEERS_TO_SHOW
  );
  if (campaignMaxPeers && campaignMaxPeers > maxPeers) {
    maxPeers = campaignMaxPeers;
  }

  const othersPhaseNumOutgoingPeerFeedback =
    othersPhaseProgress.others.peer.requested.length > maxPeers
      ? maxPeers
      : othersPhaseProgress.others.peer.requested.length;

  const othersPhaseCurrentStep = othersPhase
    ? othersPhaseProgress.others.upward.completed.length +
      othersPhaseProgress.others.peer.completed.length +
      othersPhaseProgress.others.peer.declined.length +
      enpsCompleted +
      engagementSurveyCompleted
    : -1;

  const othersPhaseMaxSteps =
    !phasesNotOpenTextDict[PHASE_TYPE_OTHERS] &&
    personIsParticipatingInOthersPhase
      ? othersPhaseProgress.others.upward.requested.length +
        othersPhaseNumOutgoingPeerFeedback +
        enpsStep +
        engagementSurveyStep
      : -1;

  const othersPhaseCurrentStepAlt = othersPhase
    ? othersPhaseProgress.others_alt.upward.completed.length +
      othersPhaseProgress.others_alt.peer.completed.length +
      othersPhaseProgress.others_alt.peer.declined.length
    : -1;

  const othersPhaseMaxStepsAlt =
    !phasesNotOpenTextDict[PHASE_TYPE_OTHERS] &&
    personIsParticipatingInOthersAltPhase
      ? othersPhaseProgress.others_alt.upward.requested.length +
        othersPhaseProgress.others_alt.peer.requested.length
      : -1;

  // only for managers; total direct reports
  const evaluationPhaseMaxSteps =
    !phasesNotOpenTextDict[PHASE_TYPE_EVALUATION] &&
    personIsParticipatingInEvaluationPhase &&
    numEligibleDirectReports > 0
      ? numEligibleDirectReports
      : -1;

  // an excluded Basics still occupies its slot in the saved current_step
  const savedSelfStep =
    !basicsStep && configs?.current_step >= 2
      ? configs.current_step - 1
      : configs?.current_step;

  const selfPhaseCurrentStep = personIsParticipatingInSelfPhase
    ? !phasesNotOpenTextDict[PHASE_TYPE_SELF] && savedSelfStep
      ? savedSelfStep <= selfPhaseMaxSteps
        ? savedSelfStep
        : selfPhaseMaxSteps
      : 0
    : -1;

  const evaluationPhaseCurrentStep = numDirectReportsCompleted;

  const personIdsToEmails = (id_list) =>
    id_list
      .map((id) => surveyResponseLookup[id]?.person?.email)
      .filter((email) => !!email);

  const outgoing_feedback_url = buildTeamDashboardLink(
    personIdsToEmails(othersPhaseProgress.others.peer.pending).map((email) => [
      'people',
      email,
    ])
  );

  const incoming_feedback_url = buildTeamDashboardLink(
    personIdsToEmails(othersPhaseProgress.others_alt.peer.pending).map(
      (email) => ['people', email]
    )
  );

  let perf_progress = [
    {
      open_text: phasesNotOpenTextDict[PHASE_TYPE_SELF],
      current_step: selfPhaseCurrentStep,
      max_steps: selfPhaseMaxSteps,
    },
    {
      outgoing_feedback_url,
      incoming_feedback_url,
      open_text: phasesNotOpenTextDict[PHASE_TYPE_OTHERS],
      current_step:
        othersPhaseProgress.others.peer.completed.length +
        othersPhaseProgress.others.peer.declined.length,
      max_steps: othersPhaseProgress.others.peer.requested.length,
      current_step_alt:
        othersPhaseProgress.others_alt.peer.completed.length +
        othersPhaseProgress.others_alt.peer.declined.length,
      max_steps_alt: othersPhaseProgress.others_alt.peer.requested.length,
      has_completed_manager_step:
        othersPhaseProgress.others.upward.requested.length > 0 &&
        othersPhaseProgress.others.upward.completed.length >=
          othersPhaseProgress.others.upward.requested.length,
      incoming_upward_current_step:
        othersPhaseProgress.others_alt.upward.completed.length +
        othersPhaseProgress.others_alt.upward.declined.length,
      incoming_upward_max_steps:
        othersPhaseProgress.others_alt.upward.requested.length,
      overall_current_step:
        othersPhaseProgress.others.upward.completed.length +
        othersPhaseProgress.others.upward.declined.length +
        othersPhaseProgress.others.peer.completed.length +
        othersPhaseProgress.others.peer.declined.length +
        enpsCompleted +
        engagementSurveyCompleted,
      overall_max_steps:
        othersPhaseProgress.others.peer.requested.length +
        othersPhaseProgress.others.upward.requested.length +
        enpsStep +
        engagementSurveyStep,
      overall_current_step_alt:
        othersPhaseProgress.others_alt.upward.completed.length +
        othersPhaseProgress.others_alt.upward.declined.length +
        othersPhaseProgress.others_alt.peer.completed.length +
        othersPhaseProgress.others_alt.peer.declined.length +
        enpsCompleted +
        engagementSurveyCompleted,
      overall_max_steps_alt:
        othersPhaseProgress.others_alt.peer.requested.length +
        othersPhaseProgress.others_alt.upward.requested.length +
        enpsStep +
        engagementSurveyStep,
      enps_completed: enpsCompleted,
      engagement_survey_completed: engagementSurveyCompleted,
    },
    {
      open_text: phasesNotOpenTextDict[PHASE_TYPE_EVALUATION],
      current_step: numDirectReportsCompleted,
      max_steps: numEligibleDirectReports,
    },
    // for now, calibration phase has nothing to show/measure
    {
      open_text: phasesNotOpenTextDict[PHASE_TYPE_CALIBRATION],
      current_step: 1,
      max_steps: 1,
    },
    {
      open_text: phasesNotOpenTextDict[PHASE_TYPE_REPORTING],
      current_step:
        (surveyResponse?.released_at ? 1 : 0) +
        (!disableConversationAcknowledgements &&
        surveyResponse?.conversation_acknowledged_at
          ? 1
          : 0),
      max_steps: disableConversationAcknowledgements ? 1 : 2,
      released_at: surveyResponse?.released_at,
      conversation_acknowledged_at:
        surveyResponse?.conversation_acknowledged_at,
    },
  ];

  if (
    othersPhaseProgress.others.peer.requested.length +
      othersPhaseProgress.others.upward.requested.length +
      othersPhaseProgress.others_alt.peer.requested.length +
      othersPhaseProgress.others_alt.upward.requested.length ===
    0
  ) {
    perf_progress[1].open_text = formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.perf_progress.no_feedback_requested',
      defaultMessage: 'No feedback to give or receive.',
    });
  }

  let influence = incomingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset &&
      // @ts-expect-error
      (r.type === RELATIONSHIP_TYPES.ENERGIZED_BY ||
        // @ts-expect-error
        r.type === RELATIONSHIP_TYPES.ADVISED_BY)
  );

  const influenceAdvised = influence.filter(
    // @ts-expect-error
    (r) => r.type === RELATIONSHIP_TYPES.ADVISED_BY
  );
  // @ts-expect-error
  const influenceAdvisedIds = influenceAdvised.map((r) => r.from_person?.email);

  const influenceEnergized = influence.filter(
    // @ts-expect-error
    (r) => r.type === RELATIONSHIP_TYPES.ENERGIZED_BY
  );
  const influenceEnergizedIds = influenceEnergized.map(
    // @ts-expect-error
    (r) => r.from_person?.email
  );

  influence = [
    // @ts-expect-error
    (influenceAdvisedIds?.length || 0) + (influenceEnergizedIds?.length || 0),
    // @ts-expect-error
    influence,
  ];

  const influence_wordcloud_skills = [
    ...(influenceAdvised ?? []),
    ...(influenceEnergized ?? []),
  ].reduce((skills, rel) => {
    // @ts-expect-error
    if (rel.positive_skills?.length) {
      const key =
        // @ts-expect-error
        rel.type === RELATIONSHIP_TYPES.ADVISED_BY
          ? 'advisedPerson'
          : 'energizedPerson';
      skills = skills.concat(
        // @ts-expect-error
        rel.positive_skills.map((skill) => ({
          // @ts-expect-error
          [key]: rel.from_person,
          wordObject: skill,
        }))
      );
    }
    return skills;
  }, []);

  const influence_skills = [
    ...(influenceAdvised ?? []),
    ...(influenceEnergized ?? []),
  ].reduce((skills, rel) => {
    // @ts-expect-error
    if (rel.positive_skills?.length) {
      // @ts-expect-error
      skills = skills.concat(rel.positive_skills);
    }
    return [...new Set(skills)];
  }, []);

  let goldstars = incomingRelationships.filter(
    // @ts-expect-error
    (r) => !r.dataset && r.type === RELATIONSHIP_TYPES.GIVES_GOLD_STAR_TO
  );
  // @ts-expect-error
  goldstars = [goldstars?.length || 0, goldstars];

  let headsups = incomingRelationships.filter(
    // @ts-expect-error
    (r) => !r.dataset && r.type === RELATIONSHIP_TYPES.GIVES_HEADS_UP_ABOUT
  );
  // @ts-expect-error
  headsups = [headsups?.length || 0, headsups];

  let formers = incomingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset && r.type === RELATIONSHIP_TYPES.USED_TO_HAVE_AS_DIRECT_REPORT
  );

  const believedManagers = outgoingRelationships
    .filter(
      // @ts-expect-error
      (r) => !r.dataset && r.type === RELATIONSHIP_TYPES.REPORTS_TO && !r.rating
    )
    // @ts-expect-error
    .map((r) => r.to_person);

  const upwardFeedbackScores = outgoingRelationships
    .filter(
      (r) =>
        // @ts-expect-error
        !r.dataset &&
        // @ts-expect-error
        r.type === RELATIONSHIP_TYPES.REPORTS_TO &&
        // @ts-expect-error
        r.rating > 0 &&
        // @ts-expect-error
        r.rating < 6
    )
    // @ts-expect-error
    .map((r) => r.rating);

  const believedManager =
    believedManagers?.length > 0 ? believedManagers[0] : undefined;

  const previousManagers = outgoingRelationships
    .filter(
      // @ts-expect-error
      (r) => !r.dataset && r.type === RELATIONSHIP_TYPES.USED_TO_REPORT_TO
    )
    // @ts-expect-error
    .map((r) => r.to_person);

  const { ifEnabled } = featuresContext;

  const additionalManagers = ifEnabled(
    'additional_managers',
    outgoingRelationships
      .filter(
        (r) =>
          // @ts-expect-error
          r.dataset &&
          // @ts-expect-error
          r.type === RELATIONSHIP_TYPES.REPORTS_TO_ADDITIONAL_MANAGER
      )
      // @ts-expect-error
      .map((r) => r.to_person),
    []
  );
  const believedAdditionalManagers = ifEnabled(
    'additional_managers',
    outgoingRelationships
      .filter(
        (r) =>
          // @ts-expect-error
          !r.dataset &&
          // @ts-expect-error
          r.type === RELATIONSHIP_TYPES.BELIEVES_TO_REPORT_TO_ADDITIONAL_MANAGER
      )
      // @ts-expect-error
      .map((r) => r.to_person),
    []
  );

  const managerRatingRelationships = incomingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset && r.type === RELATIONSHIP_TYPES.HAS_DIRECT_REPORT_FEEDBACK_FOR
  );

  const additionalManagerRatingRelationships = incomingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset &&
      // @ts-expect-error
      r.type ===
        RELATIONSHIP_TYPES.HAS_ADDITIONAL_MANAGER_DIRECT_REPORT_FEEDBACK_FOR
  );

  const managerRatingRelationship =
    managerRatingRelationships?.length > 0
      ? // Forcing a stable ordering to mirror the query in
        // views.performance_admin_calibrate_rating which, in case of multiple
        // relationships, gives precedence to the most recently created.
        managerRatingRelationships.sort(
          (a, b) =>
            // @ts-expect-error
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0]
      : null;

  let evaluationPhaseResponses = {};
  if (managerRatingRelationship) {
    evaluationPhaseResponses = {
      [EVALUATION_PHASE_QUESTION_PREFIX + 'positive_skills']:
        // @ts-expect-error
        managerRatingRelationship.positive_skills.length > 0
          ? // @ts-expect-error
            managerRatingRelationship.positive_skills
              .map((s) => s.name)
              .join(', ')
          : null,
      [EVALUATION_PHASE_QUESTION_PREFIX + 'negative_skills']:
        // @ts-expect-error
        managerRatingRelationship.negative_skills.length > 0
          ? // @ts-expect-error
            managerRatingRelationship.negative_skills
              .map((s) => s.name)
              .join(', ')
          : null,
      [EVALUATION_PHASE_QUESTION_PREFIX + 'positive_comments']:
        // @ts-expect-error
        managerRatingRelationship.positive_comments,
      [EVALUATION_PHASE_QUESTION_PREFIX + 'negative_comments']:
        // @ts-expect-error
        managerRatingRelationship.negative_comments,
    };

    // @ts-expect-error
    if (managerRatingRelationship.responses) {
      // @ts-expect-error
      for (const question in managerRatingRelationship.responses) {
        if (question.startsWith(CUSTOM_QUESTION_NONANONYMOUS_PREFIX)) {
          evaluationPhaseResponses = {
            ...evaluationPhaseResponses,
            ...getQuestionResponseDict(
              formatMessage,

              campaign,
              PHASE_TYPE_EVALUATION,
              PERFORMANCE_FEATURE_EVALUATION_OPEN_RESPONSE_QUESTIONS,
              question,
              // @ts-expect-error
              managerRatingRelationship.responses[question],
              EVALUATION_PHASE_QUESTION_PREFIX + question,
              survey_response_id,
              // @ts-expect-error
              managerRatingRelationship.from_person,
              surveyResponseCalibrationItems.filter((ci) =>
                ci.key.startsWith(question)
              ),
              NO_PRIORITY_ANSWER
            ),
          };
        }
      }
    }
  }

  const upwardFeedbackRelationships = incomingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset &&
      // @ts-expect-error
      (r.type === RELATIONSHIP_TYPES.REPORTS_TO ||
        // @ts-expect-error
        r.type === RELATIONSHIP_TYPES.REPORTS_TO_ADDITIONAL_MANAGER)
  );

  const upwardFeedbackRelationshipsWithScore =
    upwardFeedbackRelationships.filter(
      // @ts-expect-error
      (r) => r?.rating !== undefined && r?.rating !== null
    );

  let upwardFeedbackAverage = null;
  if (upwardFeedbackRelationshipsWithScore.length) {
    // @ts-expect-error
    upwardFeedbackAverage = Number(
      (
        upwardFeedbackRelationshipsWithScore.reduce(
          // @ts-expect-error
          (total, r) => total + r.rating,
          0
        ) / upwardFeedbackRelationshipsWithScore.length
      ).toFixed(1)
    );
  }

  const peerReviewRelationships = incomingRelationships.filter(
    (r) =>
      // @ts-expect-error
      !r.dataset &&
      // @ts-expect-error
      r.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR
  );

  const groupMultipleFeedback = (relationships, prefix, phase, category) => {
    let responses = {};
    if (relationships.length > 0) {
      const positiveSkills = {};
      const negativeSkills = {};
      const positiveComments = {};
      const negativeComments = {};
      let ratingsObject = {};
      const customQuestions = {};

      relationships.forEach((r, idx) => {
        const from_person = r.from_person ?? {
          full_name: formatMessage(
            {
              id: 'app.views.widgets.dashboard.team_dashboard.relationships.unattributed_person',
              defaultMessage: 'Unattributed person {number}',
            },
            {
              number: idx + 1,
            }
          ),
        };
        ratingsObject = {
          ...ratingsObject,
          ...getQuestionResponseDict(
            formatMessage,

            campaign,
            phase,
            category,
            'rating',
            r.rating,
            from_person.full_name,
            survey_response_id,
            from_person,
            [],
            NO_PRIORITY_ANSWER,
            // outputKey here is the rater's name, not the field name, so a
            // "_sortable" companion wouldn't be meaningful
            false
          ),
        };
        if (r.positive_skills.length > 0) {
          positiveSkills[from_person.full_name] = r.positive_skills.map(
            (s) => s.name
          );
        }
        if (r.negative_skills.length > 0) {
          negativeSkills[from_person.full_name] = r.negative_skills.map(
            (s) => s.name
          );
        }
        if (r.positive_comments) {
          positiveComments[from_person.full_name] = r.positive_comments;
        }
        if (r.negative_comments) {
          negativeComments[from_person.full_name] = r.negative_comments;
        }
        if (r.responses) {
          for (const question in r.responses) {
            if (question.startsWith(CUSTOM_QUESTION_NONANONYMOUS_PREFIX)) {
              if (!(prefix + question in customQuestions)) {
                customQuestions[prefix + question] = {};
              }
              customQuestions[prefix + question] = {
                ...customQuestions[prefix + question],
                ...getQuestionResponseDict(
                  formatMessage,
                  campaign,
                  phase,
                  category,
                  question,
                  r.responses[question],
                  from_person.full_name,
                  survey_response_id,
                  from_person,
                  surveyResponseCalibrationItems.filter((ci) =>
                    ci.key.startsWith(question)
                  ),
                  r.type === RELATIONSHIP_TYPES.HAS_DIRECT_REPORT_FEEDBACK_FOR
                    ? IMPORTANT_ANSWER
                    : NO_PRIORITY_ANSWER,
                  // outputKey here is the rater's name, not the field name,
                  // so a "_sortable" companion wouldn't be meaningful -- the
                  // aggregate "_sortable" is computed once the per-rater
                  // responses have been merged (see the loop over
                  // allQuestionResponses further down)
                  false
                ),
              };
            }
          }
        }
      });

      responses = {
        [prefix + 'rating']: ratingsObject,
        [prefix + 'positive_skills']: positiveSkills,
        [prefix + 'negative_skills']: negativeSkills,
        [prefix + 'positive_comments']: positiveComments,
        [prefix + 'negative_comments']: negativeComments,
      };

      const allQuestionResponses = {
        ...responses,
        ...customQuestions,
      };

      for (const cq in allQuestionResponses) {
        const questionId = cq.substring(prefix.length);
        const matchedQuestion = getQuestionFromCampaign(
          questionId,
          campaign,
          phase,
          category
        );
        if (matchedQuestion?.type?.toUpperCase() === INPUT_TYPES.LIKERT) {
          // for likert questions, cq is key, value is dict of likert responses
          // from each person of format <full_name>_<likert_value>: <response>
          // but we need to split each of these cq's into one for each likert value
          // and add them each to the responses dict
          let splitResponses = {};
          for (const personUnderscoreLikertValue in allQuestionResponses[cq]) {
            // if no response provided, just continue
            if (
              allQuestionResponses[cq][personUnderscoreLikertValue] === null
            ) {
              continue;
            }

            // split personUnderscorelikertValue into person and likertValue
            const personUnderscoreLikertValueSplit =
              personUnderscoreLikertValue.split('_');
            const person = personUnderscoreLikertValueSplit[0];
            const likertValue = personUnderscoreLikertValueSplit
              .slice(1)
              .join('_');

            const existingCalibrationInfo: CalibrationInfo | undefined =
              splitResponses[cq + '_' + likertValue]?.calibrationInfo;
            const newCalibrationInfo: CalibrationInfo | undefined =
              allQuestionResponses[cq][personUnderscoreLikertValue]
                ?.calibrationInfo;
            // add to splitResponses (and ensure we get each person's response)
            splitResponses = {
              ...splitResponses,
              [cq + '_' + likertValue]: {
                ...splitResponses[cq + '_' + likertValue],
                [person]:
                  allQuestionResponses[cq][personUnderscoreLikertValue]
                    ?.display,
                calibrationInfo: mergeCalibrationInfos(
                  existingCalibrationInfo,
                  newCalibrationInfo
                ),
              },
            };
          }

          // add an "_average" column for CSV exports for each split response
          for (const key in splitResponses) {
            const hasAverage =
              splitResponses[key] && typeof splitResponses[key] === 'object';
            if (hasAverage) {
              const calibratedValue = first(
                splitResponses[key]?.calibrationInfo?.calibrationItems ?? []
                // @ts-expect-error
              )?.value;
              splitResponses[key + '_average'] =
                calibratedValue ??
                getAverageForObjectValues(
                  omit(splitResponses[key], ['calibrationInfo'])
                );
              if (matchedQuestion?.allow_calibration) {
                splitResponses[key + SORTABLE_FIELD_SUFFIX] =
                  getCalibratableQuestionSortValue(splitResponses[key]);
              }
            }
          }

          responses = {
            ...responses,
            ...splitResponses,
          };
        } else {
          // add an "_average" column for CSV exports
          const hasAverage =
            allQuestionResponses[cq] &&
            typeof allQuestionResponses[cq] === 'object';
          if (hasAverage) {
            responses[cq + '_average'] = getAverageForObjectValues(
              allQuestionResponses[cq]
            );
            if (matchedQuestion?.allow_calibration) {
              responses[cq + SORTABLE_FIELD_SUFFIX] =
                getCalibratableQuestionSortValue(allQuestionResponses[cq]);
            }
          }

          responses[cq] = allQuestionResponses[cq];
        }
      }
    }
    return responses;
  };

  const peerFeedbackResponses = groupMultipleFeedback(
    peerReviewRelationships,
    PEER_FEEDBACK_QUESTION_PREFIX,
    PHASE_TYPE_OTHERS,
    PERFORMANCE_FEATURE_OTHERS_OPEN_RESPONSE_QUESTIONS
  );
  const upwardFeedbackResponses = groupMultipleFeedback(
    upwardFeedbackRelationships,
    UPWARD_FEEDBACK_QUESTION_PREFIX,
    PHASE_TYPE_OTHERS,
    PERFORMANCE_FEATURE_UPWARD_MANAGER_OPEN_RESPONSE_QUESTIONS
  );

  const evaluationPhaseAdditionalManagersResponses = groupMultipleFeedback(
    [...managerRatingRelationships, ...additionalManagerRatingRelationships],
    EVALUATION_PHASE_QUESTION_PREFIX,
    PHASE_TYPE_EVALUATION,
    PERFORMANCE_FEATURE_EVALUATION_OPEN_RESPONSE_QUESTIONS
  );

  // record rating even if person is terminated
  // (i.e. personIsActiveAndParticipating is false in this case)
  // NOTE: string is there for placeholder values like "RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW"
  // but any real ratings will be numbers
  let draft_rating: string | number | undefined;
  let final_rating: string | number | undefined;

  // if draft and/or final ratings were externally provided (i.e. not
  // null or indefined), set to them. NOTE: configs.rating is a number
  // if it is defined.
  if (configs.rating != null) {
    draft_rating = configs.rating;
  } else {
    if (manager) {
      // show rating if one was given
      // @ts-expect-error
      if (typeof managerRatingRelationship?.rating === 'number') {
        // @ts-expect-error
        draft_rating = managerRatingRelationship.rating;
      } else if (
        personIsParticipatingInSelfPhase ||
        personIsOnlyReceivingReview
      ) {
        if (!isDirectReportEvaluatedByManager(configs)) {
          draft_rating = RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW;
          // "is_rated" being explicitly defined as false is the only case
          // in which someone should be considered unrated
        } else if (isRatedByManager(configs)) {
          // this person requires a rating but no rating given by manager yet
          draft_rating = RATING_NOT_YET_COMPLETE;
        } else {
          // this person was manually flagged to not be able to receive a rating,
          // e.g. because they are new to their role or to the company
          draft_rating = RATING_NOT_ALLOWED;
        }
      } else {
        // this person is inactive/terminated or otherwise not participating
        // in the self phase and thus not eligible for a rating
        draft_rating = RATING_NOT_PARTICIPATING;
      }
    } else {
      // no manager, e.g. CEO, should be given this rating
      draft_rating = RATING_IGNORE_DUE_TO_NO_MANAGER;
    }
  }

  // @ts-expect-error
  let draft_rating_comments = managerRatingRelationship?.rating_comments;
  // @ts-expect-error
  const draft_rating_provided_by = managerRatingRelationship?.from_person?.id
    ? // @ts-expect-error
      managerRatingRelationship.from_person
    : undefined;

  const draftRatingAdditionalManagers =
    additionalManagerRatingRelationships.map((r) => ({
      // @ts-expect-error
      manager: r.from_person,
      // @ts-expect-error
      draft_rating: r.rating,
      // @ts-expect-error
      draft_rating_comments: r.rating_comments,
    }));

  const promotionPacketForPerson = promotionPackets.find(
    (p) =>
      peopleObjectsAreEqual(p.candidate_person, surveyResponsePerson) &&
      p.status !== PACKET_STATUS_WITHDRAWN.id &&
      p.status !== PACKET_STATUS_DENIED.id
  );

  // if packet exists for this person, override this recommendation to true
  // even if the person didn't explicitly indicate it in perf
  // (and ensure it's a boolean so CSV exports just show "TRUE")
  const extracted_recommend_for_promotion = // @ts-expect-error
    managerRatingRelationship?.recommend_for_promotion;
  let recommend_for_promotion = !!(
    extracted_recommend_for_promotion || promotionPacketForPerson
  );

  // NOTE: we don't currently ask on the frontend to provide these comments
  // so not using this for now, but leaving uncommented as we may use it
  // in the future
  // @ts-expect-error
  let promotion_comments = managerRatingRelationship?.promotion_comments;

  const recommendForPromotionAdditionalManagers =
    additionalManagerRatingRelationships.map((r) => ({
      // @ts-expect-error
      manager: r.from_person,
      // @ts-expect-error
      recommend_for_promotion: r.recommend_for_promotion,
      // @ts-expect-error
      promotion_comments: r.promotion_comments,
    }));

  let recommend_for_salary_increase =
    // @ts-expect-error
    managerRatingRelationship?.recommend_for_salary_increase;
  let salary_increase_comments =
    // @ts-expect-error
    managerRatingRelationship?.salary_increase_comments;

  const reccomentForSalaryIncreaseAdditionalManagers =
    additionalManagerRatingRelationships.map((r) => ({
      // @ts-expect-error
      manager: r.from_person,
      // @ts-expect-error
      recommend_for_salary_increase: r.recommend_for_salary_increase,
      // @ts-expect-error
      salary_increase_comments: r.salary_increase_comments,
    }));

  final_rating =
    // @ts-expect-error
    typeof managerRatingRelationship?.final_rating === 'number'
      ? // @ts-expect-error
        managerRatingRelationship.final_rating
      : // use externally-provided final rating if one exists
        // (i.e. is not null or undefined, likely string but could be
        // number, and the person wasn't manually calibrated,
        // else default to the draft rating. NOTE: configs.final_rating
        // is a number if it is defined.
        configs.final_rating != null
        ? configs.final_rating
        : draft_rating;
  // @ts-expect-error
  let final_rating_comments = managerRatingRelationship?.final_rating_comments;
  const final_rating_provided_by_person = // @ts-expect-error
    managerRatingRelationship?.final_rating_provided_by_person?.id
      ? // @ts-expect-error
        managerRatingRelationship?.final_rating_provided_by_person
      : undefined;

  const was_manually_calibrated =
    // @ts-expect-error
    typeof managerRatingRelationship?.final_rating === 'number';

  // calculate percentages
  let perf_progress_self_phase =
    phasesNotOpenTextDict[PHASE_TYPE_SELF] || !personIsParticipatingInSelfPhase
      ? undefined
      : Math.floor((selfPhaseCurrentStep * 100) / selfPhaseMaxSteps);
  let perf_progress_others_phase = othersPhase
    ? phasesNotOpenTextDict[PHASE_TYPE_OTHERS] ||
      !personIsParticipatingInOthersPhase
      ? undefined
      : othersPhaseMaxSteps === 0
        ? undefined // there is no feedback requested, consider them "not participating"
        : (configs?.phase_others_complete && 100) || // phase is complete based on survey response
          Math.floor((othersPhaseCurrentStep * 100) / othersPhaseMaxSteps)
    : -1;

  let perf_progress_others_phase_alt = othersPhase
    ? phasesNotOpenTextDict[PHASE_TYPE_OTHERS] ||
      !personIsParticipatingInOthersAltPhase
      ? undefined
      : othersPhaseMaxStepsAlt === 0
        ? undefined // there is no incoming feedback expected, consider them "not participating"
        : Math.floor((othersPhaseCurrentStepAlt * 100) / othersPhaseMaxStepsAlt)
    : -1;
  let perf_progress_evaluation_phase =
    phasesNotOpenTextDict[PHASE_TYPE_EVALUATION] ||
    !personIsParticipatingInEvaluationPhase ||
    evaluationPhaseMaxSteps <= 0
      ? undefined
      : Math.floor(
          (evaluationPhaseCurrentStep * 100) / evaluationPhaseMaxSteps
        );

  // for now, calibration phase has nothing to show/measure
  // TODO: show calibration phase progress for calibrators
  let perf_progress_calibration_phase = 100;

  let perf_progress_reporting_phase =
    phasesNotOpenTextDict[PHASE_TYPE_REPORTING] ||
    !personIsParticipatingInSelfPhase
      ? undefined
      : (surveyResponse?.released_at ? 50 : 0) +
        (surveyResponse?.conversation_acknowledged_at ? 50 : 0);

  // the rightmost fields are randomized for demo purposes if it's demo mode
  if (campaign.status == CAMPAIGN_STATUSES.DEMO) {
    perf_progress = [
      // @ts-expect-error
      {
        current_step: Math.floor(Math.random() * Math.floor(6)),
        max_steps: 6,
      },
      // @ts-expect-error
      {
        current_step: Math.floor(Math.random() * Math.floor(6)),
        max_steps: 6,
      },
      // @ts-expect-error
      {
        current_step: Math.floor(Math.random() * Math.floor(6)),
        max_steps: 6,
      },
      // @ts-expect-error
      {
        current_step: 1,
        max_steps: 1,
      },
      // @ts-expect-error
      {
        current_step: Math.floor(Math.random() * Math.floor(1)),
        max_steps: 1,
      },
    ];

    perf_progress_self_phase =
      perf_progress[0].current_step / perf_progress[0].max_steps;
    perf_progress_others_phase =
      // @ts-expect-error
      perf_progress[1].overall_current_step /
      // @ts-expect-error
      perf_progress[1].overall_max_steps;
    perf_progress_others_phase_alt =
      // @ts-expect-error
      perf_progress[1].overall_current_step_alt /
      // @ts-expect-error
      perf_progress[1].overall_max_steps_alt;
    perf_progress_evaluation_phase =
      perf_progress[2].current_step / perf_progress[2].max_steps;
    perf_progress_calibration_phase =
      perf_progress[3].current_step / perf_progress[3].max_steps;
    perf_progress_reporting_phase =
      perf_progress[4].current_step / perf_progress[4].max_steps;

    recommend_for_promotion = Math.floor(Math.random() * Math.floor(2)) === 1;
    promotion_comments = formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.promotion_comments.placeholder',
      defaultMessage: '[Promotion comments left will appear here]',
    });
    recommend_for_salary_increase =
      Math.floor(Math.random() * Math.floor(2)) === 1;
    salary_increase_comments = formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.salary_increase_comments.placeholder',
      defaultMessage: '[Salary increase comments left will appear here]',
    });
    // @ts-expect-error
    influence = [0, []];
    // @ts-expect-error
    goldstars = [0, []];
    // @ts-expect-error
    headsups = [0, []];
    formers = [];
    draft_rating = Math.floor(Math.random() * Math.floor(5));
    draft_rating_comments = formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.draft_rating_comments.placeholder',
      defaultMessage: '[Draft rating comments left will appear here]',
    });
    final_rating = draft_rating;
    final_rating_comments = formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.final_rating_comments.placeholder',
      defaultMessage: '[Final rating comments left will appear here]',
    });
  }

  // actual perf is only phases 1-3 (4 is calibration, 5 are conversations)
  const perf_progress_overall =
    typeof perf_progress_self_phase === 'undefined' ||
    typeof perf_progress_others_phase === 'undefined' ||
    typeof perf_progress_evaluation_phase === 'undefined'
      ? undefined
      : (perf_progress_self_phase +
          perf_progress_others_phase +
          perf_progress_evaluation_phase) /
        3;

  const endOfPerfCampaignCoveragePeriod = new Date(campaign.coverage_end_date);

  const personFields = person;

  const fieldsToUseFromConfigs = [
    ...PERSON_METADATA_FIELDS,
    'manager_external_id',
    'manager_effective_date',
    'hrbp',
    'leader',
    'employee_number',
    'current_status',
  ];

  // set manager and other personal metadata based on snapshot from campaign
  // (which could differ from live/current data)
  // The below are items in the survey response config (which is saved when the dataset
  // relationships are updated in the backend)
  // NOTE: we don't use configs?.given_name because
  // this data isn't as trusted as the given_name set
  // in Confirm.
  fieldsToUseFromConfigs.forEach((field) => {
    // NOTE: if the item is unpopulated on the person we default to what is on the campaigm
    if (shouldSourceMetadataFromCampaignUnlessUndefined && configs[field]) {
      personFields[field] = configs[field];
    }
  });

  const believedManagerIfDifferent =
    !believedManager || peopleObjectsAreEqual(manager, believedManager)
      ? undefined
      : believedManager;

  const personShouldGiveUpwardFeedback =
    configs?.is_writing_upward_manager_review !== false;

  const full_team_url = buildTeamDashboardLink([
    ['manager_or_above', person.email],
  ]);
  const direct_reports_url = buildTeamDashboardLink([
    ['manager', person.email],
  ]);
  const date_for_time_in_role = getDateForTimeInRoleComputation(personFields);

  // auto-calibration
  const can_auto_calibrate =
    draft_rating !== RATING_NOT_ALLOWED &&
    draft_rating !== RATING_NOT_PARTICIPATING &&
    draft_rating !== RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW &&
    draft_rating !== RATING_NOT_YET_COMPLETE &&
    draft_rating !== RATING_IGNORE_DUE_TO_NO_MANAGER;

  const is_calibrated =
    can_auto_calibrate &&
    (was_manually_calibrated ||
      (!calibrationFlagsRecomputing &&
        !surveyResponse?.calibration_flags?.length));

  const genericCalibrationFlagNames = {
    person: formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.calibration_flags.generic_person',
      defaultMessage: 'Person',
    }),
    manager: formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.calibration_flags.generic_manager',
      defaultMessage: 'Manager',
    }),
  };

  const calibration_flags_recomputing =
    can_auto_calibrate && calibrationFlagsRecomputing;

  const [calibrationFlags, missingCalibrationFlags] =
    can_auto_calibrate && !calibration_flags_recomputing
      ? getCalibrationFlags(
          surveyResponse?.calibration_flags || [],
          preCompiledautoCalibrationRules,
          {
            person: person.given_name,
            manager: manager?.given_name || genericCalibrationFlagNames.manager,
          },
          genericCalibrationFlagNames
        )
      : [[], []];

  const calibrationPrefix = formatMessage({
    id: 'app.views.widgets.dashboard.team_dashboard.filters.calibration',
    defaultMessage: 'Calibration:',
  });

  const noFlagsOriginallyArray = [
    calibrationPrefix +
      ' ' +
      formatMessage({
        id: 'app.views.widgets.dashboard.team_dashboard.filters.no_flags_raise_originally',
        defaultMessage: 'No flags raised originally',
      }),
  ];

  const calibrationFlagsForFiltersIfAny = getCalibrationFlags(
    surveyResponse?.calibration_flags || [],
    preCompiledautoCalibrationRules,
    genericCalibrationFlagNames,
    genericCalibrationFlagNames
  )[0].map((s) => calibrationPrefix + ' ' + s);

  const calibrationFlagsForFilters = calibration_flags_recomputing
    ? [
        calibrationPrefix +
          ' ' +
          formatMessage({
            id: 'app.views.widgets.dashboard.team_dashboard.filters.recalculating',
            defaultMessage: 'Recalculating',
          }),
      ]
    : calibrationFlagsForFiltersIfAny?.length > 0
      ? calibrationFlagsForFiltersIfAny
      : noFlagsOriginallyArray;

  const calibrationSortPrimary = calibrationFlags?.length ?? 0;
  const calibrationSortSecondary = final_rating;

  const can_edit_final_rating =
    !surveyResponse?.released_at &&
    final_rating !== RATING_NOT_YET_COMPLETE &&
    final_rating !== RATING_IGNORE_DUE_TO_NO_MANAGER &&
    final_rating !== RATING_NOT_ELIGIBLE_TO_MANAGER_REVIEW &&
    final_rating !== RATING_NOT_PARTICIPATING;

  let calibrationStatus;
  if (can_auto_calibrate) {
    if (was_manually_calibrated) {
      calibrationStatus = CALIBRATION_STATUS_CALIBRATED;
    } else if (calibration_flags_recomputing) {
      calibrationStatus = CALIBRATION_STATUS_RECALCULATING;
    } else if (calibrationFlags?.length) {
      calibrationStatus = CALIBRATION_STATUS_NEEDS_ATTENTION;
    } else {
      calibrationStatus = CALIBRATION_STATUS_NO_FLAGS_RAISED;
    }
  }

  const isWritingPeerReviews =
    !!getPersonIsParticipatingInPhase(person, configs, othersPhase) &&
    configs.is_writing_peer_reviews !== false;

  const isReceivingPeerReviews = configs.is_receiving_peer_reviews !== false;

  // Peers
  const peerFeedbackAttribution =
    getCampaignFeature(
      campaign,
      PERFORMANCE_FEATURE_PEER_FEEDBACK_ATTRIBUTION
    ) ?? 'unattributed';
  const isPeerFeedbackAttributed = peerFeedbackAttribution !== 'unattributed';

  const incomingPeers = incomingRelationships
    .filter(
      (r) =>
        // @ts-expect-error
        r.dataset &&
        // @ts-expect-error
        r.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR
    )
    .sort(sortRelationshipsByPriority)
    .map((r) => {
      const isCompleted =
        !!campaign &&
        relationshipIsCompleted(
          incomingRelationships.find(
            (ir) =>
              // @ts-expect-error
              ir.type ===
                RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR &&
              // @ts-expect-error
              !ir.dataset &&
              // @ts-expect-error
              ir.from_person?.id === r.from_person.id
          ),
          campaign
        );
      const isDeclined = incomingRelationships.some(
        (ir) =>
          // @ts-expect-error
          ir.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR &&
          // @ts-expect-error
          !!ir.dataset &&
          // @ts-expect-error
          ir.from_person.id === r.from_person.id &&
          // @ts-expect-error
          !!ir.decline_reason
      );
      return {
        // @ts-expect-error
        ...r.from_person,
        is_completed: isCompleted,
        is_declined: isDeclined,
        overlay: isDeclined ? 'fail' : isCompleted ? 'checked' : undefined,
        overlayCallback: isPeerFeedbackAttributed ? peerOverlayCallback : null,
      };
    });

  const outgoingPeers = outgoingRelationships
    .filter(
      (r) =>
        // @ts-expect-error
        r.dataset &&
        // @ts-expect-error
        r.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR
    )
    .sort(sortRelationshipsByPriority)
    .map((r) => {
      const isCompleted =
        !!campaign &&
        relationshipIsCompleted(
          outgoingRelationships.find(
            (or) =>
              // @ts-expect-error
              or.type ===
                RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR &&
              // @ts-expect-error
              !or.dataset &&
              // @ts-expect-error
              or.to_person.id === r.to_person.id
          ),
          campaign
        );
      const isDeclined = outgoingRelationships.some(
        (or) =>
          // @ts-expect-error
          or.type === RELATIONSHIP_TYPES.IS_CHOSEN_TO_WRITE_PEER_FEEDBACK_FOR &&
          // @ts-expect-error
          !!or.dataset &&
          // @ts-expect-error
          or.to_person.id === r.to_person.id &&
          // @ts-expect-error
          !!or.decline_reason
      );
      return {
        // @ts-expect-error
        ...r.to_person,
        is_completed: isCompleted,
        is_declined: isDeclined,
        overlay: isDeclined ? 'fail' : isCompleted ? 'checked' : undefined,
        overlayCallback: isPeerFeedbackAttributed ? peerOverlayCallback : null,
      };
    });

  const output = {
    ...personFields,
    // change person and manager urls to point to "/performance" underneath the
    // user which has all the context for what they wrote and others wrote about them
    url: person.url, // + '/performance',
    title: configs?.title || person.title,
    full_url: process.env.REACT_APP_CONFIRM_APP_URL + person.url,
    full_team_url,
    direct_reports_url,
    manager: manager,
    manager_full_name: manager?.full_name,
    manager_email: manager?.email,
    manager_title: manager?.title,
    believed_manager: believedManagerIfDifferent,
    believed_manager_full_name: believedManagerIfDifferent?.full_name,
    believed_manager_email: believedManagerIfDifferent?.email,
    believed_manager_title: believedManagerIfDifferent?.title,
    previous_managers: previousManagers,
    previous_managers_text: previousManagers
      ?.map((p) => p?.full_name)
      ?.join(', '),
    additional_managers: additionalManagers,
    additional_managers_text: additionalManagers
      ?.map((p) => p?.full_name)
      ?.join(', '),
    believed_additional_managers: believedAdditionalManagers,
    believed_additional_managers_text: believedAdditionalManagers
      .map((p) => p?.full_name)
      ?.join(', '),

    overall_tenure: personFields?.latest_hire_date
      ? monthDiff(
          new Date(personFields.latest_hire_date),
          endOfPerfCampaignCoveragePeriod
        )
      : undefined,
    role_tenure: date_for_time_in_role
      ? monthDiff(
          new Date(date_for_time_in_role),
          endOfPerfCampaignCoveragePeriod
        )
      : undefined,

    is_only_receiving_review: personIsOnlyReceivingReview,
    is_active_and_participating_in_any_phase:
      personIsActiveAndParticipatingInAnyPhase,
    is_active_and_participating_in_self_phase: personIsParticipatingInSelfPhase,
    is_active_and_participating_in_others_phase:
      personIsParticipatingInOthersPhase,
    is_active_and_participating_in_others_alt_phase:
      personIsParticipatingInOthersAltPhase,
    is_active_and_participating_in_evaluation_phase:
      personIsParticipatingInEvaluationPhase,
    is_writing_upward_manager_review: personShouldGiveUpwardFeedback,
    is_writing_peer_reviews: isWritingPeerReviews,
    is_receiving_peer_reviews: isReceivingPeerReviews,
    is_rated: isRatedByManager(configs),
    is_evaluated_by_manager: isDirectReportEvaluatedByManager(configs),

    perf_progress_completions: perfProgressCompletions,
    perf_progress: perf_progress,
    perf_progress_self_phase: perf_progress_self_phase,
    perf_progress_others_phase: perf_progress_others_phase,
    perf_progress_others_phase_gave_manager_feedback:
      perf_progress_others_phase_gave_manager_feedback,
    perf_progress_others_phase_outgoing_peers_assigned:
      perf_progress_others_phase_outgoing_peers_assigned,
    perf_progress_others_phase_outgoing_peers_completed:
      perf_progress_others_phase_outgoing_peers_completed,
    perf_progress_others_phase_outgoing_peers_declined:
      perf_progress_others_phase_outgoing_peers_declined,

    perf_progress_others_phase_alt_peer_count:
      perf_progress[1].current_step_alt,
    perf_progress_others_phase_alt_manager_count:
      perf_progress[1].incoming_upward_current_step,
    perf_progress_others_phase_alt: perf_progress_others_phase_alt,
    perf_progress_evaluation_phase: perf_progress_evaluation_phase,
    perf_progress_calibration_phase: perf_progress_calibration_phase,
    perf_progress_reporting_phase: perf_progress_reporting_phase,
    perf_progress_overall: perf_progress_overall,

    recommend_for_promotion: recommend_for_promotion,
    extracted_recommend_for_promotion: extracted_recommend_for_promotion,
    recommend_for_promotion_sort_value: recommend_for_promotion
      ? promotionPacketForPerson
        ? // @ts-expect-error
          PACKET_STATUSES.find((s) => s.id === promotionPacketForPerson?.status)
            .sortOrder
        : 0
      : 1,

    promotion_comments: promotion_comments,
    recommend_for_promotion_additional_managers:
      recommendForPromotionAdditionalManagers,
    promotion_packet_status:
      promotionPacketForPerson &&
      PACKET_STATUSES.find((s) => s.id === promotionPacketForPerson?.status)
        ?.headingPlain,

    recommend_for_salary_increase: recommend_for_salary_increase,
    recommend_for_salary_increase_additional_managers:
      reccomentForSalaryIncreaseAdditionalManagers,
    recommend_for_salary_increase_sort_value: recommend_for_salary_increase
      ? 0
      : 1,

    salary_increase_comments: salary_increase_comments,

    influence: influence,
    ...formatCSVONARelationshipByType(
      'influence_ids',
      influence,
      formatMessage,
      formatGiverEmail
    ),
    influence_advised: influenceAdvised,
    influence_advised_ids: influenceAdvisedIds.join(', '),
    influence_energized: influenceEnergized,
    influence_energized_ids: influenceEnergizedIds.join(', '),
    influence_count: influence[0] || 0,
    ...formatCSVONACountsByType('influence_count', influence, formatMessage),
    influence_wordcloud_skills,
    influence_skills,
    goldstars: goldstars,
    goldstars_count: goldstars[0] || 0,
    ...formatCSVONACountsByType('goldstars_count', goldstars, formatMessage),
    goldstars_comments: (isRawValueFollowedByOutputValueArrayOfTwo(goldstars)
      ? goldstars[1]
      : []
    )?.reduce((str, gs) => {
      return (
        (str?.length > 0 ? str + '\n' : '') +
        // @ts-expect-error
        gs.from_person?.full_name +
        ': ' +
        // @ts-expect-error
        (gs.positive_comments
          ? // @ts-expect-error
            gs.positive_comments
          : formatMessage({
              id: 'app.views.widgets.dashboard.team_dashboard.goldstars_comments.placeholder.no_comments',
              defaultMessage: 'No comments provided',
            }))
      );
    }, ''),
    ...formatCSVONARelationshipByType(
      'goldstars_comments',
      goldstars,
      formatMessage,
      defaultFormatRelationshipItem(
        (rel) => rel.comments,
        formatMessage({
          id: 'app.views.widgets.dashboard.team_dashboard.goldstars_comments.placeholder.no_comments',
          defaultMessage: 'No comments provided',
        })
      )
    ),
    goldstars_skills: (isRawValueFollowedByOutputValueArrayOfTwo(goldstars)
      ? goldstars[1]
      : []
    )?.reduce((str, gs) => {
      return (
        (str?.length > 0 ? str + '\n' : '') +
        // @ts-expect-error
        gs.from_person?.full_name +
        ': ' +
        // @ts-expect-error
        (gs.positive_skills?.length > 0
          ? // @ts-expect-error
            gs.positive_skills.map((s) => s.name).join(', ')
          : formatMessage({
              id: 'app.views.widgets.dashboard.team_dashboard.goldstars_skills.placeholder.no_skills',
              defaultMessage: 'No skills provided',
            }))
      );
    }, ''),
    ...formatCSVONARelationshipByType(
      'goldstars_skills',
      goldstars,
      formatMessage,
      defaultFormatRelationshipItem(
        formatSkills,
        formatMessage({
          id: 'app.views.widgets.dashboard.team_dashboard.goldstars_skills.placeholder.no_skills',
          defaultMessage: 'No skills provided',
        })
      )
    ),
    headsups: headsups,
    headsups_count: headsups[0] || 0,
    ...formatCSVONACountsByType('headsups_count', headsups, formatMessage),
    headsups_comments: (isRawValueFollowedByOutputValueArrayOfTwo(headsups)
      ? headsups[1]
      : []
    )?.reduce((str, hu) => {
      return (
        (str?.length > 0 ? str + '\n' : '') +
        // @ts-expect-error
        hu.from_person?.full_name +
        ': ' +
        // @ts-expect-error
        (hu.negative_comments
          ? // @ts-expect-error
            hu.negative_comments
          : formatMessage({
              id: 'app.views.widgets.dashboard.team_dashboard.headsups_comments.placeholder.no_comments',
              defaultMessage: 'No comments provided',
            }))
      );
    }, ''),
    ...formatCSVONARelationshipByType(
      'headsups_comments',
      headsups,
      formatMessage,
      defaultFormatRelationshipItem(
        (rel) => rel.comments,
        formatMessage({
          id: 'app.views.widgets.dashboard.team_dashboard.headsups_comments.placeholder.no_comments',
          defaultMessage: 'No comments provided',
        })
      )
    ),
    headsups_skills: (isRawValueFollowedByOutputValueArrayOfTwo(headsups)
      ? headsups[1]
      : []
    )?.reduce((str, hu) => {
      return (
        (str?.length > 0 ? str + '\n' : '') +
        // @ts-expect-error
        hu.from_person?.full_name +
        ': ' +
        // @ts-expect-error
        (hu.negative_skills?.length > 0
          ? // @ts-expect-error
            hu.negative_skills.map((s) => s.name).join(', ')
          : formatMessage({
              id: 'app.views.widgets.dashboard.team_dashboard.headsups_skills.placeholder.no_skills',
              defaultMessage: 'No skills provided',
            }))
      );
    }, ''),
    ...formatCSVONARelationshipByType(
      'headsups_skills',
      headsups,
      formatMessage,
      defaultFormatRelationshipItem(
        formatSkills,
        formatMessage({
          id: 'app.views.widgets.dashboard.team_dashboard.headsups_skills.placeholder.no_skills',
          defaultMessage: 'No skills provided',
        })
      )
    ),
    formers: formers,
    formers_count: formers?.length ? formers.length : 0,
    formers_comments: formers?.reduce((str, fo) => {
      return (
        (str?.length > 0 ? str + '\n' : '') +
        // @ts-expect-error
        fo.from_person?.full_name +
        ': ' +
        // @ts-expect-error
        (fo.positive_comments
          ? // @ts-expect-error
            fo.positive_comments
          : formatMessage({
              id: 'app.views.widgets.dashboard.team_dashboard.formers_comments.placeholder.no_comments',
              defaultMessage: 'No comments provided',
            }))
      );
    }, ''),
    formers_skills: formers?.reduce((str, fo) => {
      return (
        (str?.length > 0 ? str + '\n' : '') +
        // @ts-expect-error
        fo.from_person?.full_name +
        ': ' +
        // @ts-expect-error
        (fo.positive_skills?.length > 0
          ? // @ts-expect-error
            fo.positive_skills.map((s) => s.name).join(', ')
          : formatMessage({
              id: 'app.views.widgets.dashboard.team_dashboard.formers_skills.placeholder.no_skills',
              defaultMessage: 'No skills provided',
            }))
      );
    }, ''),
    upwardFeedbackAverage: upwardFeedbackAverage,
    upwardFeedbackRelationshipsWithScore: upwardFeedbackRelationshipsWithScore,
    draft_rating: draft_rating,
    draft_rating_sortable: getSortableRating(draft_rating),
    draft_rating_comments: draft_rating_comments,
    draft_rating_provided_by: draft_rating_provided_by,
    draft_rating_provided_by_full_name: draft_rating_provided_by?.full_name,
    draft_rating_additional_managers: draftRatingAdditionalManagers,
    // @ts-expect-error
    is_calibration_actioned: managerRatingRelationship?.is_calibration_actioned, // Synthetic flag for people that se themselves and have obfuscated data
    final_rating: final_rating,
    final_rating_comments: final_rating_comments,
    final_rating_provided_by_person: final_rating_provided_by_person,
    final_rating_provided_by_person_full_name:
      final_rating_provided_by_person?.full_name,
    final_rating_sortable: getSortableRating(final_rating),

    // for showing in filters
    final_rating_text: getFinalRatingText(final_rating, formatMessage),

    // note: final/previous ratings could be 0 or negative
    draft_to_final_delta:
      typeof final_rating !== 'number' ||
      typeof draft_rating !== 'number' ||
      final_rating === draft_rating
        ? undefined
        : final_rating - draft_rating,

    previous_final_rating:
      typeof configs.previous_final_rating === 'number'
        ? configs.previous_final_rating
        : undefined,

    // note: final/previous ratings could be 0 or negative
    previous_to_final_delta:
      typeof final_rating !== 'number' ||
      typeof configs.previous_final_rating !== 'number' ||
      final_rating === configs.previous_final_rating
        ? undefined
        : final_rating - configs.previous_final_rating,
    ...(Object.entries(ratingsHistory?.ratings?.[person.id] ?? {}).reduce(
      (acc, [previous_campaign_id, value]) => {
        // @ts-expect-error
        acc[`ratings_history_${previous_campaign_id}`] = value.rating;
        return acc;
      },
      {}
    ) || []),
    // use numbers for proper ordering in UI when sorted
    share_status: surveyResponse?.conversation_acknowledged_at
      ? surveyResponse?.conversation_rating
        ? surveyResponse.conversation_rating
        : SHARE_STATUS_ACKNOWLEDGED_WITHOUT_RATING
      : surveyResponse.released_at
        ? SHARE_STATUS_RELEASED
        : manager
          ? // people who are only receiving reviews should be eligible to
            // have their report released
            personIsParticipatingInSelfPhase || personIsOnlyReceivingReview
            ? configs?.withhold_report_release
              ? SHARE_STATUS_WITHHOLD_REPORT_RELEASE
              : SHARE_STATUS_ONLY_RECEIVING_REVIEWS
            : SHARE_STATUS_NOT_PARTICIPATING
          : SHARE_STATUS_IGNORE_DUE_TO_NO_MANAGER,

    // end-of-perf report releasing and conversations-related tracking
    withhold_report_release: configs?.withhold_report_release,
    released_at: surveyResponse?.released_at,
    conversation_acknowledged_at: surveyResponse?.conversation_acknowledged_at,
    conversation_rating: surveyResponse?.conversation_rating,
    conversation_comments: surveyResponse?.conversation_comments,
    conversation_visibility: surveyResponse?.conversation_visibility,

    // get associated promotion packet if one exists
    packet: promotionPacketForPerson,

    // score for upward feedback
    upward_feedback_score: upwardFeedbackScores[0] || undefined,

    survey_response_id: survey_response_id,
    // pass in full configs of survey response to use for
    // report releasing logic
    configs: surveyResponse?.configs,

    ...selfPhaseResponses,
    ...upwardFeedbackResponses,
    ...peerFeedbackResponses,
    ...ifEnabled(
      'additional_managers',
      evaluationPhaseAdditionalManagersResponses,
      evaluationPhaseResponses
    ),
    // auto-calibration
    can_auto_calibrate,
    calibration_flags_recomputing,
    current_calibration_flags: is_calibrated ? [] : calibrationFlags,
    current_missing_calibration_flags: is_calibrated
      ? []
      : missingCalibrationFlags,
    original_calibration_flags: calibrationFlags,
    original_missing_calibration_flags: missingCalibrationFlags,
    calibration_text: calibrationFlagsForFilters,
    calibration_status: calibrationStatus,
    calibration_flag_sort_primary: calibrationSortPrimary,
    calibration_flag_sort_secondary: calibrationSortSecondary,
    can_edit_final_rating,
    report_release_status: surveyResponse?.released_at
      ? REPORT_STATUS_RELEASED
      : REPORT_STATUS_NOT_RELEASED,
    report_acknowledgement_status: disableConversationAcknowledgements
      ? null
      : surveyResponse?.conversation_acknowledged_at
        ? REPORT_STATUS_CONVERSATION_ACKNOWLEDGED
        : REPORT_STATUS_CONVERSATION_NOT_ACKNOWLEDGED,
    evaluations_done: numDirectReportsCompleted,
    evaluations_owed: numEligibleDirectReports,

    // peer feedback
    incoming_peers: incomingPeers,
    outgoing_peers: outgoingPeers,
    incoming_peers_sort_value: (incomingPeers ?? []).length,
    outgoing_peers_sort_value: (outgoingPeers ?? []).length,
  };

  // Append custom campaign data values (prefixed to avoid key conflicts)
  const customerDataColumns = campaign?.customer_campaign_data_columns;
  if (customerDataColumns) {
    const personCustomData = configs?._customer_data;
    for (const col of customerDataColumns) {
      output[`_customer_data_${col.key}`] = personCustomData?.[col.key];
    }
  }

  if (customColumnsGenerators) {
    customColumnsGenerators.forEach((generator) => {
      const field_name = generator.columnConfig.field;
      const value = generator.generateValue({
        formatMessage,
        shouldSourceMetadataFromCampaignUnlessUndefined,
        campaign,
        phasesNotOpenTextDict,
        surveyResponse,
        incomingRelationships,
        outgoingRelationships,
        promotionPackets,
        demoPeople,
        surveyResponseLookup,
        disableConversationAcknowledgements,
        completion_stats,
        enpsResponsesLookup,
        engagementSurveyResponsesLookup,
        preCompiledautoCalibrationRules,
        featuresContext,
        surveyResponseCalibrationItems,
      });
      output[field_name] = value;
      if (generator.toSortValue) {
        output[field_name + SORTABLE_FIELD_SUFFIX] =
          generator.toSortValue(value);
      }
    });
  }

  return output;
};

export const getCalibrationStatusDescriptionFromValue = (
  value,
  formatMessage
) => {
  if (value === CALIBRATION_STATUS_NO_FLAGS_RAISED) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.calibration_no_flags_raised_currently',
      defaultMessage: 'No flags raised currently',
    });
  }
  if (value === CALIBRATION_STATUS_RECALCULATING) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.calibration_recalculating',
      defaultMessage: 'Recalculating',
    });
  }
  if (value === CALIBRATION_STATUS_NEEDS_ATTENTION) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.calibration_needs_attention',
      defaultMessage: 'Needs attention',
    });
  }
  if (value === CALIBRATION_STATUS_CALIBRATED) {
    return formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.calibration_calibrated',
      defaultMessage: 'Calibrated',
    });
  }

  return null;
};

export const getReportReleaseStatusDescriptionFromValue = (
  value,
  formatMessage
) => {
  const descriptions = {
    [REPORT_STATUS_NOT_RELEASED]: formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.report_status.not_released',
      defaultMessage: 'Report not yet released',
    }),
    [REPORT_STATUS_RELEASED]: formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.report_status.released',
      defaultMessage: 'Report released',
    }),
  };

  return descriptions[value] ?? null;
};

export const getReportAcknowledgementStatusDescriptionFromValue = (
  value,
  formatMessage
) => {
  const descriptions = {
    [REPORT_STATUS_CONVERSATION_NOT_ACKNOWLEDGED]: formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.report_status.not_acknowledged',
      defaultMessage: 'Conversation not yet acknowledged',
    }),
    [REPORT_STATUS_CONVERSATION_ACKNOWLEDGED]: formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.report_status.acknowledged',
      defaultMessage: 'Conversation acknowledged',
    }),
  };

  return descriptions[value] ?? null;
};

const getReportReleaseStatusIconFromValue = (value) => {
  const descriptions = {
    [REPORT_STATUS_NOT_RELEASED]: ICONS.REPORT_STATUS_NOT_RELEASED,
    [REPORT_STATUS_RELEASED]: ICONS.REPORT_STATUS_RELEASED,
  };

  return descriptions[value] ?? null;
};

const getReportAcknowledgementStatusIconFromValue = (value) => {
  const descriptions = {
    [REPORT_STATUS_CONVERSATION_NOT_ACKNOWLEDGED]:
      ICONS.REPORT_STATUS_CONVERATION_NOT_ACKNOWLEDGED,
    [REPORT_STATUS_CONVERSATION_ACKNOWLEDGED]:
      ICONS.REPORT_STATUS_CONVERATION_ACKNOWLEDGED,
  };

  return descriptions[value] ?? null;
};

export const fullNameForPeopleFilter = (
  fieldName,
  fieldValue,
  formatMessage
) => {
  switch (fieldName) {
    case 'people':
    case 'person':
      return fieldValue.full_name;
    case 'manager':
      return formatMessage(
        {
          id: 'app.views.widgets.dashboard.team_dashboard.direct_reports',
          defaultMessage: "{personName}'s direct reports",
        },
        { personName: fieldValue.full_name }
      );
    case 'manager_or_above':
      return formatMessage(
        {
          id: 'app.views.widgets.dashboard.team_dashboard.full_team',
          defaultMessage: "{personName}'s full team",
        },
        {
          personName: fieldValue.full_name,
        }
      );
    case 'final_rating_provided_by_person':
      return formatMessage(
        {
          id: 'app.views.widgets.dashboard.team_dashboard.final_rating_provided_by_person',
          defaultMessage: '{personName} calibrated final rating',
        },
        {
          personName: fieldValue.full_name,
        }
      );
  }

  return null;
};

export const getItemFromField = (fieldName, fieldValue, formatMessage) => {
  const iconName = getIconForFieldName(fieldName);
  const item = {
    name: fieldValue,
    icon: iconName,
    description: getDescriptionForFieldName(fieldName, formatMessage),
    filterObject: {
      key: fieldName,
      value: fieldValue,
      keyDescription: getDescriptionForFieldName(fieldName, formatMessage),
    },
  };
  if (
    fieldName === 'people' ||
    fieldName === 'manager' ||
    fieldName === 'manager_or_above' ||
    fieldName === 'final_rating_provided_by_person'
  ) {
    return {
      ...item,
      _index: 'person',
      isPerson: true,
      filterObject: {
        key: fieldName,
        value: fieldValue.id,
        keyDescription: getEnhancedDescriptionForFieldName(
          fieldName,
          formatMessage
        ),
      },
      object: {
        avatar: fieldValue.avatar,
        title: fieldValue.title || '',
        full_name: fullNameForPeopleFilter(
          fieldName,
          fieldValue,
          formatMessage
        ),
        email: fieldValue.email,
        full_name_when_selected: fieldValue.full_name,
      },
      // need to pass id for uniqueness checking
      id: fieldValue.id,
      name: fieldValue.full_name,
    };
  } else if (fieldName === 'calibration_status') {
    const calibrationPrefix = formatMessage({
      id: 'app.views.widgets.dashboard.team_dashboard.filters.calibration',
      defaultMessage: 'Calibration:',
    });
    return {
      ...item,
      name:
        calibrationPrefix +
        ' ' +
        getCalibrationStatusDescriptionFromValue(fieldValue, formatMessage),
    };
  } else if (fieldName === 'report_release_status') {
    return {
      ...item,
      icon: getReportReleaseStatusIconFromValue(fieldValue),
      name: getReportReleaseStatusDescriptionFromValue(
        fieldValue,
        formatMessage
      ),
    };
  } else if (fieldName === 'report_acknowledgement_status') {
    return {
      ...item,
      icon: getReportAcknowledgementStatusIconFromValue(fieldValue),
      name: getReportAcknowledgementStatusDescriptionFromValue(
        fieldValue,
        formatMessage
      ),
    };
  }

  return item;
};

export const itemsForFieldNameAreEqual = (fieldName, a, b) => {
  if (!a && !b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  if (isPersonType(fieldName)) {
    return peopleObjectsAreEqual(a, b);
  }

  return a === b;
};

export const accumulateSuggestionsForManagers = (p, acc) => {
  if (
    p['manager'] &&
    acc['manager'].findIndex((v) =>
      itemsForFieldNameAreEqual('manager', v, p['manager'])
    ) === -1
  ) {
    acc['manager'].push(p['manager']);
  }
  if (p['additional_managers']) {
    for (const additionalManager of p['additional_managers']) {
      if (
        acc['manager'].findIndex((v) =>
          itemsForFieldNameAreEqual('manager', v, additionalManager)
        ) === -1
      ) {
        acc['manager'].push(additionalManager);
      }
    }
  }
};

export const TEAM_DASHBOARD_MATCHES_FILTER_OPTIONS: FilterOptions = {
  fieldTransformers: {
    people: (value, operator) => {
      return `(${operator == 'EQ' ? '==' : '!='} "${
        value.filterObject.value
      }" id)`;
    },
    // The manager picked is a person of today, while the emails in the
    // configs are the dataset's snapshot: the two are matched on the person
    // id, which holds when an email is stale or has changed.
    manager: (value, operator) => {
      if (value.id == null) {
        return `(${operator == 'EQ' ? '==' : '!='} "${
          value.object.email
        }" configs.manager_email)`;
      }
      return `(${operator == 'EQ' ? '==' : '!='} "${value.id}" manager.id)`;
    },
    manager_or_above: (value, operator) => {
      const matcher = operator == 'EQ' ? 'in' : 'not-in';
      const clauses = [
        ...(value.id == null
          ? []
          : [
              `(${matcher} "${value.id}" configs.manager_or_above_ids)`,
              `(${matcher} "${value.id}" configs.additional_managers_or_above_ids)`,
            ]),
        ...(value.object?.email
          ? [
              `(${matcher} "${value.object.email}" configs.manager_or_above)`,
              `(${matcher} "${value.object.email}" configs.additional_managers_or_above)`,
            ]
          : []),
      ];
      return `(${operator == 'EQ' ? 'or' : 'and'} ${clauses.join(' ')})`;
    },
    calibration_text: (value, operator) => {
      return `(${operator == 'EQ' ? 'in' : 'not-in'} "${
        value.filterObject.value
      }" calibration_text)`;
    },
    final_rating_provided_by_person: (value, operator) => {
      return `(${operator == 'EQ' ? '==' : '!='} "${
        value.filterObject.value
      }" final_rating_provided_by_person.id)`;
    },
  },
};

export const personMatchesAdvancedFilter = (
  filter,
  person,
  options: FilterOptions = TEAM_DASHBOARD_MATCHES_FILTER_OPTIONS
) => {
  return matchesParsed(filter, person, options);
};

// list of all fields that could be outputted in the CSV or
// in the table in the UI so we can remove them from
// the UI and/or CSV if they aren't found in the data
// to keep the UI and CSV clean of unneeded fields
export const potentialFieldsInCsvOrUITable = [
  'external_id',
  'given_name',
  'family_name',
  'full_name',
  'email',
  'title',
  'full_url',
  'manager_full_name',
  'manager_email',
  'manager_title',
  'believed_manager_full_name',
  'believed_manager_email',
  'believed_manager_title',
  'previous_managers_text',
  'additional_managers_text',
  'level_id',
  'level',
  'function',
  'cost_center',
  'country',
  'department',
  'business_unit',
  'employee_number',
  'perf_progress_self_phase',
  'perf_progress_others_phase',
  'perf_progress_others_phase_gave_manager_feedback',
  'perf_progress_others_phase_outgoing_peers_assigned',
  'perf_progress_others_phase_outgoing_peers_completed',
  'perf_progress_others_phase_outgoing_peers_declined',
  'perf_progress_others_phase_alt_count',
  'perf_progress_others_phase_alt',
  'perf_progress_evaluation_phase',
  'perf_progress_reporting_phase',
  'perf_progress_overall',
  'team_perf_completed_self_phase',
  'team_perf_completed_others_phase',
  'team_perf_completed_others_phase_alt',
  'team_perf_completed_evaluation_phase',
  'team_perf_total_eligible',
  'team_perf_total_eligible_evaluation_phase',
  'team_perf_completed_self_phase_percent',
  'team_perf_completed_others_phase_percent',
  'team_perf_completed_others_phase_percent_alt',
  'team_perf_completed_evaluation_phase_percent',
  'team_perf_completed_reporting_phase_percent',
  'overall_tenure',
  'role_tenure',
  'influence_advised_ids',
  'influence_energized_ids',
  'influence_count',
  'goldstars_count',
  'headsups_count',
  'formers_count',
  'goldstars_comments',
  'headsups_comments',
  'formers_comments',
  'goldstars_skills',
  'headsups_skills',
  'formers_skills',
  'recommend_for_promotion',
  'promotion_comments',
  'promotion_packet_status',
  'recommend_for_salary_increase',
  'salary_increase_comments',
  'draft_rating',
  'draft_rating_provided_by_full_name',
  'draft_rating_comments',
  'previous_final_rating',
  'final_rating',
  'final_rating_comments',
  'final_rating_provided_by_person_full_name',
  'previous_to_final_delta',
  'draft_to_final_delta',
  'released_at',
  'conversation_acknowledged_at',
  'conversation_rating',
  'conversation_comments',
  'conversation_visibility',
  'location',
  'management_tier',
  'original_hire_date',
  'latest_hire_date',
  'position_effective_date',
  'position_start_date',
  'manager_effective_date',
  'cost_center_id',
  'department_id',
  'business_unit_id',
  'classification',
  'status',
  'hrbp',
  'leader',
  'incoming_peers',
  'outgoing_peers',
];

const fieldExclusions: { [key: string]: WizardTypes[] } = {
  perf_progress_self_phase: [
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_others_phase: [WIZARD_TYPE_ONA_ONLY],
  perf_progress_others_phase_gave_manager_feedback: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_others_phase_outgoing_peers_assigned: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_others_phase_outgoing_peers_completed: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_others_phase_alt_peer_count: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_others_phase_alt_manager_count: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_others_phase_outgoing_peers_declined: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_evaluation_phase: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  perf_progress_reporting_phase: [
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  team_perf_completed_self_phase: [
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  team_perf_completed_others_phase: [WIZARD_TYPE_ONA_ONLY],
  team_perf_completed_others_phase_alt: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
  ],
  perf_progress_others_phase_alt_count: [WIZARD_TYPE_ONA_ONLY],
  perf_progress_others_phase_alt: [WIZARD_TYPE_ONA_ONLY],
  team_perf_completed_evaluation_phase: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  team_perf_total_eligible_evaluation_phase: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  team_perf_completed_self_phase_percent: [
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  team_perf_completed_others_phase_percent: [WIZARD_TYPE_ONA_ONLY],
  team_perf_completed_others_phase_percent_alt: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
  ],
  team_perf_completed_evaluation_phase_percent: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  team_perf_completed_reporting_phase_percent: [
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  influence_advised_ids: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  influence_energized_ids: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  influence_count: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  goldstars_count: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  headsups_count: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  goldstars_comments: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  headsups_comments: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  goldstars_skills: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  headsups_skills: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  recommend_for_promotion: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  promotion_comments: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  promotion_packet_status: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  recommend_for_salary_increase: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  salary_increase_comments: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  draft_rating: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  draft_rating_provided_by_full_name: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  draft_rating_comments: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  previous_final_rating: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  final_rating: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  final_rating_comments: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  final_rating_provided_by_person_full_name: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  previous_to_final_delta: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  draft_to_final_delta: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  released_at: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  conversation_acknowledged_at: [
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  conversation_rating: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  conversation_comments: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  conversation_visibility: [WIZARD_TYPE_ENGAGEMENT_ONLY, WIZARD_TYPE_ENPS_ONLY],
  incoming_peers: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
  outgoing_peers: [
    WIZARD_TYPE_ONA_ONLY,
    WIZARD_TYPE_ENGAGEMENT_ONLY,
    WIZARD_TYPE_ENPS_ONLY,
  ],
};

export function isFieldExcluded(
  field: string,
  wizardType: WizardTypes
): boolean {
  const exclusions = fieldExclusions[field];
  if (!exclusions) return false;
  return exclusions.includes(wizardType);
}

// computes the "average"/effective value for a custom question response,
// preferring an explicit calibration override when one exists; shared by
// the cell display logic below and getCalibratableQuestionSortValue
export const computeCustomQuestionAverageValue = (
  val: any,
  calibrationInfo: CalibrationInfo | undefined
) => {
  if (
    isStrictlyObject(val) &&
    !!calibrationInfo &&
    !!calibrationInfo.calibrationItems?.length
  ) {
    return first(calibrationInfo.calibrationItems)?.value;
  }

  const average = getAverageForObjectValues(val);

  if (
    !!average &&
    !!calibrationInfo &&
    !!calibrationInfo.priorityCurrentValue
  ) {
    return calibrationInfo.priorityCurrentValue;
  }

  return average;
};

// sortable value for a calibratable custom-question column, matching what's
// shown in the cell: whenever calibrationInfo is present, the cell renders
// TeamDashboardCalibrationEditor (see allowCalibration below), whose
// displayed value is priorityCurrentValue ?? currentValue; calibrationInfo
// is otherwise absent only when there's no response to calibrate yet, in
// which case fall back to the same average/raw value used for icon display
export const getCalibratableQuestionSortValue = (rawFieldValue) => {
  const { val, calibrationInfo } = decoupleValAndCalibrationInfo(rawFieldValue);

  if (calibrationInfo) {
    return calibrationInfo.priorityCurrentValue ?? calibrationInfo.currentValue;
  }

  if (val === null || typeof val === 'undefined') {
    return undefined;
  }

  if (typeof val !== 'object') {
    return val;
  }

  if (isRawValueFollowedByOutputValueArrayOfTwo(val)) {
    return val[0];
  }

  if (Array.isArray(val) && val.every((v) => typeof v === 'object')) {
    // e.g. people-editor responses; not meaningfully sortable
    return undefined;
  }

  return computeCustomQuestionAverageValue(val, calibrationInfo);
};

export const generateColumnsForCustomQuestions = (
  questionsList,
  prefixKey,
  prefixHeaderIconClassName: string,
  prefixHeaderPopoverPrefix,
  prefixCsvLabel,
  hasMultipleResults,
  showCustomCalibration
) => {
  return questionsList
    ?.filter((q) => q.type?.toUpperCase() !== INPUT_TYPES.SECTION)
    .reduce((acc, q, index) => {
      const questionNumber = index + 1;
      const fieldName = prefixKey + q.key;
      const fullHeaderLabel = (
        <>
          <i className={prefixHeaderIconClassName} />
          {questionNumber}
        </>
      );
      const nonHtmlLabel = stripHtml(q.label);

      const responseOutput = {
        // columns for visual display
        name: fullHeaderLabel,
        fullName: (
          <>
            {fullHeaderLabel} {nonHtmlLabel}
          </>
        ),
        className: 'text-start text-nowrap',
        field: fieldName,
        format: (
          value,
          uniqueIndexIdentifier,
          _rowIndex,
          rowValue,
          expanded
        ) => {
          const { val, calibrationInfo } = decoupleValAndCalibrationInfo(value);

          const uniquePopoverId = fieldName + '-' + uniqueIndexIdentifier;
          let averageValue;
          let outputPopoverContent;
          let popoverClassName = '';

          if (val && typeof val === 'object') {
            // if this is an array and first value is not object, then
            // first value is raw value and second value is output value
            if (isRawValueFollowedByOutputValueArrayOfTwo(val)) {
              averageValue = val[0];
              outputPopoverContent = val[1];
            } else {
              // people editor objects is list of people
              if (
                Array.isArray(val) &&
                val.every((v) => typeof v === 'object')
              ) {
                if (val.every((v) => v.full_name)) {
                  outputPopoverContent = (
                    <PersonCardList
                      people={val}
                      stacked={true}
                      inPopover={true}
                    />
                  );
                  popoverClassName = 'mx-n3 my-n2';
                } else {
                  // any other list of objects; shouldn't ever be hit
                  // as it's likely that the actual data being saved is
                  // bad (e.g. should use positive_skills / negative_skills
                  // instead of a custom question), so throw error
                  console.error(
                    'Unexpected perf admin custom question data; likely need to fix question for campaign and fix data from all survey responses.'
                  );
                  outputPopoverContent = val
                    .map((v) => {
                      if (v.full_name) {
                        return v.full_name;
                      } else if (v.name) {
                        return v.name;
                      } else {
                        return v;
                      }
                    })
                    .join(', ');
                }
              } else {
                const valKeys = Object.keys(val);

                // if every value in the object is parsable into a number, get
                // the average and display it
                averageValue = computeCustomQuestionAverageValue(
                  val,
                  calibrationInfo
                );

                const displayValue = (value) => {
                  if (isRawValueFollowedByOutputValueArrayOfTwo(value)) {
                    const [raw, display] = value;
                    // don't show if "raw" value is uuid-based id
                    if (raw.startsWith('id_')) {
                      return display;
                    } else {
                      return `(${raw}) ${display}`;
                    }
                  } else {
                    if (Array.isArray(value)) {
                      return value.join(', ');
                    } else {
                      if (typeof value === 'boolean') {
                        return value ? (
                          <FormattedMessage
                            id="app.views.widgets.dashboards.team_dashboard.yes"
                            defaultMessage="Yes"
                          />
                        ) : (
                          <FormattedMessage
                            id="app.views.widgets.dashboards.team_dashboard.no"
                            defaultMessage="No"
                          />
                        );
                      }
                      return value;
                    }
                  }
                };

                // note: if value is array, first value is id of output (used for showing average in the UI)
                // and second value is the actual value for display
                outputPopoverContent = (
                  <div>
                    <CalibrationPopover
                      calibrationInfo={calibrationInfo}
                      question={q}
                    />
                    {valKeys.map((key, index) => (
                      <div className={index === 0 ? '' : 'mt-3'} key={index}>
                        <span className="fw-bold">
                          {key}
                          <FormattedMessage
                            id="app.views.widgets.dashboards.team_dashboard.colon"
                            defaultMessage=":"
                          />
                        </span>{' '}
                        {displayValue(val[key])}
                      </div>
                    ))}
                  </div>
                );
              }
            }
          }

          // show no icon if no data to show
          if (
            (typeof averageValue === 'undefined' || averageValue === null) &&
            typeof outputPopoverContent === 'undefined' &&
            (typeof val === 'undefined' || val === null)
          ) {
            return undefined;
          }

          const shouldShowIcon =
            q.type === INPUT_TYPES.DROPDOWN ||
            q.type === INPUT_TYPES.MULTIPLE_CHOICE ||
            q.type === INPUT_TYPES.SELECT ||
            averageValue === undefined ||
            averageValue === null;

          const allowCalibration = !!q?.allow_calibration && !!calibrationInfo;
          const editableCalibration =
            showCustomCalibration && !!rowValue?.can_edit_final_rating;

          // the full response content, used either inline or inside the popover
          const responseContent = (
            <>
              {outputPopoverContent}
              {typeof outputPopoverContent === 'undefined' && (
                <RichTextViewer model={val} expanded={true} />
              )}
            </>
          );

          // when inline display is requested, render the response content
          // directly in the cell instead of behind the hover notepad icon.
          // only applies where a notepad icon would otherwise be shown (i.e.
          // open-text / choice responses); average cells are left untouched.
          if (expanded && shouldShowIcon && !allowCalibration) {
            return (
              <div
                className="d-flex align-items-center"
                style={{ whiteSpace: 'normal' }}
              >
                {responseContent}
                {!!calibrationInfo && (
                  <span className="ms-1 text-muted">
                    {formatChangeDelta(q, calibrationInfo)}
                  </span>
                )}
              </div>
            );
          }

          // show either icon or average and have full content in popover
          return (
            <>
              <div id={uniquePopoverId} className="d-flex align-items-center">
                {allowCalibration ? (
                  <TeamDashboardCalibrationEditor
                    editable={editableCalibration}
                    targetPerson={rowValue}
                    question={q}
                    calibrationInfo={calibrationInfo}
                  />
                ) : (
                  <>
                    {shouldShowIcon ? (
                      <i
                        style={{ position: 'relative', left: '3px' }}
                        className="fe fe-file-text"
                      />
                    ) : (
                      averageValue
                    )}
                  </>
                )}
                {!!calibrationInfo && (
                  <span className="ms-1 text-muted">
                    {formatChangeDelta(q, calibrationInfo)}
                  </span>
                )}
              </div>
              <ScrollableUncontrolledPopover
                placement="left"
                trigger="hover"
                target={uniquePopoverId}
                className="popover-variable-lg"
                innerClassName={popoverClassName}
              >
                {responseContent}
              </ScrollableUncontrolledPopover>
            </>
          );
        },
        popoverContent: (
          <>
            {prefixHeaderPopoverPrefix}
            {nonHtmlLabel}
          </>
        ),

        // CSV columns
        label: prefixCsvLabel + ' #' + questionNumber + ': ' + nonHtmlLabel,
        key: fieldName,
        calibrationField: q?.allow_calibration,
        sortField: q?.allow_calibration
          ? fieldName + SORTABLE_FIELD_SUFFIX
          : undefined,

        // for filtering (see buildCustomQuestionFilterConfig)
        question: q,
        filterIcon: prefixHeaderIconClassName,
        // likert responses are numbers, so the column supports range filters
        ...(q.type?.toUpperCase() === INPUT_TYPES.LIKERT
          ? {
              numeric: true,
              filterDescription:
                prefixCsvLabel + ' #' + questionNumber + ': ' + nonHtmlLabel,
            }
          : {}),
      };

      if (hasMultipleResults) {
        // add CSV-only output of the average for multiple responses
        const averageKey = fieldName + '_average';
        const averageOutput = {
          csvOnly: true,
          field: averageKey,
          label: prefixCsvLabel + ' #' + questionNumber + ' average',
          key: averageKey,
        };

        // add column for the average value of the question
        // followed by the individual responses as its own column
        return [...acc, responseOutput, averageOutput];
      }

      // no average for single responses
      return [...acc, responseOutput];
    }, []);
};

type DashboardData = {
  [k: string]: any;
};

export const expandDashboardData = (
  data: DashboardData,
  formatMessage: IntlShape['formatMessage'],
  formatNumber: IntlShape['formatNumber']
): DashboardData => {
  if (data?.relationships) {
    const relationship_person_keys = [
      'from_person',
      'to_person',
      'final_rating_provided_by_person',
    ];

    for (const relationship of data.relationships) {
      for (const key of relationship_person_keys) {
        if (relationship[key]) {
          const person_id: number = relationship[key]['id'];
          relationship[key] = data.people_from_relationships[person_id];
        }
      }
    }

    delete data.people_from_relationships;
  }

  if (data?.employee_nps) {
    if (data.employee_nps?.by_field?.tenure) {
      const buckets = Object.keys(data.employee_nps.by_field.tenure).filter(
        (x) => x !== CATCHALL_GROUP_NAME
      );
      const x = buckets.reduce((acc, bucket) => {
        data.employee_nps.by_field.tenure[bucket].sort_key = bucket;
        acc[formatTenureBucket(bucket, formatMessage, formatNumber)] =
          data.employee_nps.by_field.tenure[bucket];
        return acc;
      }, {});
      if (data.employee_nps.by_field.tenure[CATCHALL_GROUP_NAME]) {
        x[CATCHALL_GROUP_NAME] =
          data.employee_nps.by_field.tenure[CATCHALL_GROUP_NAME];
      }
      data.employee_nps.by_field.tenure = x;
    }

    if (data.employee_nps?.comments) {
      // iterate trouhgh comments and format tenure buckets
      for (const comment of data.employee_nps.comments) {
        if (
          comment.keys.tenure &&
          comment.keys.tenure !== CATCHALL_GROUP_NAME
        ) {
          comment.keys.tenure__sort_key = comment.keys.tenure;
          comment.keys.tenure = formatTenureBucket(
            comment.keys.tenure,
            formatMessage,
            formatNumber
          );
        }
      }
    }
  }

  if (data?.engagement_survey) {
    for (const val of data.engagement_survey) {
      if (val.responses?.tenure) {
        const buckets = Object.keys(val.responses?.tenure).filter(
          (x) => x !== '_others'
        );
        const x = buckets.reduce((acc, bucket) => {
          val.responses.tenure[bucket].sort_key = bucket;
          acc[formatTenureBucket(bucket, formatMessage, formatNumber)] =
            val.responses.tenure[bucket];
          return acc;
        }, {});
        if (val.responses.tenure._others) {
          if (val.responses.tenure._others?.included_filter_values?.tenure) {
            val.responses.tenure._others.included_filter_values.tenure =
              val.responses.tenure._others.included_filter_values.tenure.map(
                (bucket) =>
                  formatTenureBucket(bucket, formatMessage, formatNumber)
              );
          }
          x['_others'] = val.responses.tenure._others;
        }
        val.responses.tenure = x;
      }

      if (val._all_by_breakdown?.tenure?.excluded_filter_values?.tenure) {
        val._all_by_breakdown.tenure.excluded_filter_values.tenure =
          val._all_by_breakdown.tenure.excluded_filter_values.tenure.map(
            (bucket) => formatTenureBucket(bucket, formatMessage, formatNumber)
          );
      }

      if (val._all_by_breakdown_org?.tenure?.excluded_filter_values?.tenure) {
        val._all_by_breakdown_org.tenure.excluded_filter_values.tenure =
          val._all_by_breakdown_org.tenure.excluded_filter_values.tenure.map(
            (bucket) => formatTenureBucket(bucket, formatMessage, formatNumber)
          );
      }
    }
  }

  return data;
};

const formatTenureBucket = (
  key: string,
  formatMessage: IntlShape['formatMessage'],
  formatNumber: IntlShape['formatNumber']
) => {
  const groups = key.split('_');
  // removes the sort value
  groups.shift();
  if (groups.length === 3) {
    const value = parseInt(groups[1], 10);
    const unit = groups[2];
    if (groups[0] === 'from') {
      return formatMessage(
        {
          id: '"app.views.widgets.dashboards.team_dashboard.tenure_bucket.from',
          defaultMessage: 'More than {start}',
          description: 'Tenure bucket for more than X time',
        },
        {
          start: formatNumber(value, {
            style: 'unit',
            unitDisplay: 'long',
            unit,
          }),
        }
      );
    } else if (groups[0] === 'to') {
      return formatMessage(
        {
          id: '"app.views.widgets.dashboards.team_dashboard.tenure_bucket.to',
          defaultMessage: 'Less than {end}',
          description: 'Tenure bucket for less than X time',
        },
        {
          end: formatNumber(value, {
            style: 'unit',
            unitDisplay: 'long',
            unit,
          }),
        }
      );
    }
  } else if (groups.length === 6) {
    const startValue = parseInt(groups[1], 10);
    const startUnit = groups[2];
    const endValue = parseInt(groups[4], 10);
    const endUnit = groups[5];

    return formatMessage(
      {
        id: '"app.views.widgets.dashboards.team_dashboard.tenure_bucket.from_to',
        defaultMessage: 'From {start} to {end}',
        description: 'Tenure bucket between X and Y time',
      },
      {
        start: formatNumber(startValue, {
          style: 'unit',
          unitDisplay: 'long',
          unit: startUnit,
        }),
        end: formatNumber(endValue, {
          style: 'unit',
          unitDisplay: 'long',
          unit: endUnit,
        }),
      }
    );
  }
  throw new Error('Malformed tenure bucket key: ' + key);
};
